import React, { useState, useEffect } from 'react';
import NegationGroupsAdmin from './NegationGroupsAdmin';
import PositiveGroupsAdmin from './PositiveGroupsAdmin';
import ConnectorAdmin from './ConnectorAdmin';
import SentenceConstructionAdmin from './SentenceConstructionAdmin';
import PronounMappingsAdmin from './PronounMappingsAdmin';

const GrammarAdminTab = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [verbs, setVerbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedVerb, setExpandedVerb] = useState(null);
  const [editingVerb, setEditingVerb] = useState(null);
  const [editVerbName, setEditVerbName] = useState('');
  const [renamingVerb, setRenamingVerb] = useState(false);
  const [selectedVerbs, setSelectedVerbs] = useState(new Set());
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('verbs'); // 'verbs', 'draft', 'add', 'test', 'rules'

  // Draft verbs state
  const [draftVerbs, setDraftVerbs] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftExpandedVerb, setDraftExpandedVerb] = useState(null);
  const [draftActiveTense, setDraftActiveTense] = useState('past');
  const [seedingDrafts, setSeedingDrafts] = useState(false);
  const [draftEditMode, setDraftEditMode] = useState(null); // verb_group being edited
  const [draftEditData, setDraftEditData] = useState({}); // {entryId: {latinScript, translatedText}}
  const [stats, setStats] = useState({ total_verbs: 0, total_conjugations: 0 });

  // Audit state
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Usage: setConfirmDialog({ title, message, icon, onConfirm, confirmText, confirmColor })

  const showConfirmDialog = (opts) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        ...opts,
        onConfirm: () => { setConfirmDialog(null); resolve(true); },
        onCancel: () => { setConfirmDialog(null); resolve(false); }
      });
    });
  };

  // Negation Groups state

  // Grammar Rules state
  const [grammarRules, setGrammarRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: '', type: 'skip_word', pattern: '', context: '', action: 'skip',
    replacement: '', description: '', examples: [{ input: '', expected: '' }],
    enabled: true, priority: 10
  });

  // Sentence Patterns state
  const [sentencePatterns, setSentencePatterns] = useState([]);
  const [patternsLoading, setPatternsLoading] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [newPattern, setNewPattern] = useState({
    name: '', type: 'wh_question', trigger: '', latin_translation: '',
    arabic_translation: '', pattern: '', description: '',
    examples: [{ input: '', expected: '' }], enabled: true, priority: 10
  });
  const [patternTestText, setPatternTestText] = useState('');
  const [patternTestResult, setPatternTestResult] = useState(null);
  const [patternTestLoading, setPatternTestLoading] = useState(false);
  const [showPatternHelp, setShowPatternHelp] = useState(false);

  // Verb Subjunctive state
  const [verbSubjunctives, setVerbSubjunctives] = useState([]);
  const [subjLoading, setSubjLoading] = useState(false);
  const [editingSubj, setEditingSubj] = useState(null);
  const [newSubj, setNewSubj] = useState({
    verb: '',
    forms: {
      i: { latin: '', arabic: '' },
      you: { latin: '', arabic: '' },
      he: { latin: '', arabic: '' },
      she: { latin: '', arabic: '' },
      we: { latin: '', arabic: '' },
      they: { latin: '', arabic: '' }
    }
  });

  // Verb + Object Pronoun Patterns state
  const [verbObjectPatterns, setVerbObjectPatterns] = useState([]);
  const [verbObjLoading, setVerbObjLoading] = useState(false);
  const [newVerbObjName, setNewVerbObjName] = useState('');
  const [editingVerbObj, setEditingVerbObj] = useState(null);
  const [editingVerbObjData, setEditingVerbObjData] = useState({});

  // Skip characters (Cleanup tab) state
  const [skipChars, setSkipChars] = useState([]);
  const [skipCharsEnabled, setSkipCharsEnabled] = useState(true);
  const [newSkipChar, setNewSkipChar] = useState('');

  const fetchSkipChars = async () => {
    try {
      const res = await fetch(`${API}/admin/grammar/skip-chars`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSkipChars(data.chars || []);
        setSkipCharsEnabled(data.enabled !== false);
      }
    } catch (err) {
      console.error('Error fetching skip chars:', err);
    }
  };

  const saveSkipChars = async (chars, enabled) => {
    try {
      const res = await fetch(`${API}/admin/grammar/skip-chars`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ chars, enabled })
      });
      if (res.ok) {
        showNotification && showNotification('Skip characters saved', 'success');
      }
    } catch (err) {
      showNotification && showNotification('Error saving skip characters', 'error');
    }
  };

  const addSkipChar = () => {
    const ch = newSkipChar.trim();
    if (!ch || skipChars.includes(ch)) return;
    const updated = [...skipChars, ch];
    setSkipChars(updated);
    setNewSkipChar('');
    saveSkipChars(updated, skipCharsEnabled);
  };

  const removeSkipChar = (ch) => {
    const updated = skipChars.filter(c => c !== ch);
    setSkipChars(updated);
    saveSkipChars(updated, skipCharsEnabled);
  };

  const toggleSkipCharsEnabled = () => {
    const newVal = !skipCharsEnabled;
    setSkipCharsEnabled(newVal);
    saveSkipChars(skipChars, newVal);
  };

  // Fetch grammar rules
  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/rules`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGrammarRules(data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
    }
    setRulesLoading(false);
  };

  // Save new rule
  const saveNewRule = async () => {
    if (!newRule.name || !newRule.pattern) {
      showNotification && showNotification('Name and pattern are required', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/grammar/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          ...newRule,
          examples: newRule.examples.filter(e => e.input.trim())
        })
      });
      if (res.ok) {
        showNotification && showNotification('Grammar rule added!', 'success');
        setNewRule({ name: '', type: 'skip_word', pattern: '', context: '', action: 'skip', replacement: '', description: '', examples: [{ input: '', expected: '' }], enabled: true, priority: 10 });
        fetchRules();
      } else {
        const err = await res.json();
        showNotification && showNotification(err.detail || 'Failed to add rule', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error saving rule', 'error');
    }
  };

  // Update rule
  const updateRule = async (ruleId, updates) => {
    try {
      const res = await fetch(`${API}/admin/grammar/rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        showNotification && showNotification('Rule updated!', 'success');
        fetchRules();
        setEditingRule(null);
      }
    } catch (err) {
      showNotification && showNotification('Error updating rule', 'error');
    }
  };

  // Delete rule
  const deleteRule = async (ruleId, ruleName) => {
    if (!window.confirm(`Delete rule "${ruleName}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/grammar/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Rule deleted', 'success');
        fetchRules();
      }
    } catch (err) {
      showNotification && showNotification('Error deleting rule', 'error');
    }
  };

  // ── Sentence Patterns CRUD ──────────────────────────────────────
  const fetchPatterns = async () => {
    setPatternsLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/patterns`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSentencePatterns(data.patterns || []);
      }
    } catch (err) {
      console.error('Error fetching patterns:', err);
    }
    setPatternsLoading(false);
  };

  const saveNewPattern = async () => {
    if (!newPattern.name || !newPattern.trigger) {
      showNotification && showNotification('Name and trigger are required', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/grammar/patterns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          ...newPattern,
          examples: newPattern.examples.filter(e => e.input.trim())
        })
      });
      if (res.ok) {
        showNotification && showNotification('Sentence pattern added!', 'success');
        setNewPattern({ name: '', type: 'wh_question', trigger: '', latin_translation: '', arabic_translation: '', pattern: '', description: '', examples: [{ input: '', expected: '' }], enabled: true, priority: 10 });
        fetchPatterns();
      } else {
        const err = await res.json();
        showNotification && showNotification(err.detail || 'Failed to add pattern', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error saving pattern', 'error');
    }
  };

  const updatePattern = async (patternId, updates) => {
    try {
      const res = await fetch(`${API}/admin/grammar/patterns/${patternId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        showNotification && showNotification('Pattern updated!', 'success');
        fetchPatterns();
        setEditingPattern(null);
      }
    } catch (err) {
      showNotification && showNotification('Error updating pattern', 'error');
    }
  };

  const deletePattern = async (patternId, patternName) => {
    if (!window.confirm(`Delete pattern "${patternName}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/grammar/patterns/${patternId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Pattern deleted', 'success');
        fetchPatterns();
      }
    } catch (err) {
      showNotification && showNotification('Error deleting pattern', 'error');
    }
  };

  const handleTestPattern = async () => {
    if (!patternTestText.trim()) return;
    setPatternTestLoading(true);
    setPatternTestResult(null);
    try {
      const res = await fetch(`${API}/admin/grammar/test-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ text: patternTestText })
      });
      const data = await res.json();
      if (res.ok) {
        setPatternTestResult(data);
      } else {
        setPatternTestResult({ success: false, message: data.detail || 'Server error' });
      }
    } catch (err) {
      setPatternTestResult({ success: false, message: 'Network error: ' + err.message });
    }
    setPatternTestLoading(false);
  };

  // ── Verb Subjunctive CRUD ─────────────────────────────────────
  const fetchSubjunctives = async () => {
    setSubjLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/verb-subjunctives`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVerbSubjunctives(data.verbs || []);
      }
    } catch (err) {
      console.error('Error fetching subjunctives:', err);
    }
    setSubjLoading(false);
  };

  const fetchVerbObjectPatterns = async () => {
    setVerbObjLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/verb-object-patterns`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVerbObjectPatterns(data.patterns || []);
      }
    } catch (err) {
      console.error('Error fetching verb-object patterns:', err);
    }
    setVerbObjLoading(false);
  };

  const saveNewSubjunctive = async () => {
    if (!newSubj.verb.trim()) {
      showNotification && showNotification('Verb name is required', 'error');
      return;
    }
    // Check at least one form has data
    const hasAnyForm = Object.values(newSubj.forms).some(f => f.latin.trim() || f.arabic.trim());
    if (!hasAnyForm) {
      showNotification && showNotification('Enter at least one pronoun form', 'error');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/grammar/verb-subjunctives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(newSubj)
      });
      if (res.ok) {
        showNotification && showNotification(`Subjunctive forms for "${newSubj.verb}" added!`, 'success');
        setNewSubj({ verb: '', forms: { i: { latin: '', arabic: '' }, you: { latin: '', arabic: '' }, he: { latin: '', arabic: '' }, she: { latin: '', arabic: '' }, we: { latin: '', arabic: '' }, they: { latin: '', arabic: '' } } });
        fetchSubjunctives();
      } else {
        const err = await res.json();
        showNotification && showNotification(err.detail || 'Failed to add', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error saving subjunctive', 'error');
    }
  };

  const updateSubjunctive = async (id, updates) => {
    try {
      const res = await fetch(`${API}/admin/grammar/verb-subjunctives/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        showNotification && showNotification('Verb updated!', 'success');
        fetchSubjunctives();
        setEditingSubj(null);
      }
    } catch (err) {
      showNotification && showNotification('Error updating verb', 'error');
    }
  };

  const deleteSubjunctive = async (id, verb) => {
    if (!window.confirm(`Delete subjunctive forms for "${verb}"?`)) return;
    try {
      const res = await fetch(`${API}/admin/grammar/verb-subjunctives/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Verb subjunctive deleted', 'success');
        fetchSubjunctives();
      }
    } catch (err) {
      showNotification && showNotification('Error deleting verb', 'error');
    }
  };

  // Test with rules
  const handleTestWithRules = async () => {
    if (!testText.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API}/admin/grammar/test-with-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ text: testText })
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, message: 'Error testing' });
    }
    setTestLoading(false);
  };

  // Edit state
  const [editingConj, setEditingConj] = useState(null); // ID of conjugation being edited
  const [editForm, setEditForm] = useState({
    sourceText: '',
    latinScript: '',
    translatedText: '',
    gender: '',
    number: ''
  });
  const [saving, setSaving] = useState(false);

  // Create new verb inline
  const [newVerbName, setNewVerbName] = useState('');
  // Paste state: tense tab + both scripts
  const [pasteTarget, setPasteTarget] = useState(null); // kept for backwards compat
  const [pasteText, setPasteText] = useState('');
  const [pasteSaving, setPasteSaving] = useState(false);
  const [pasteTab, setPasteTab] = useState('past_pos'); // active tense+form tab in expanded view
  const [baseForm, setBaseForm] = useState({ latinM: '', latinF: '', arabicM: '', arabicF: '' });
  const [baseSaving, setBaseSaving] = useState(false);
  const [pasteLatinText, setPasteLatinText] = useState('');
  const [pasteArabicText, setPasteArabicText] = useState('');
  const emptyTenseData = { past: { latin: '', arabic: '' }, present: { latin: '', arabic: '' }, future: { latin: '', arabic: '' } };
  const [allTenseData, setAllTenseData] = useState({ ...emptyTenseData });
  // Negative generation state — per-tense: { past: [...], present: [...], future: [...] }
  const [negativeProposals, setNegativeProposals] = useState({});
  const [negativePattern, setNegativePattern] = useState({});
  const [negModal, setNegModal] = useState(null); // { positiveLatin, verbName, tense, generating }
  const [negModalInput, setNegModalInput] = useState('');
  const [negDropPronoun, setNegDropPronoun] = useState(false); // Keep pronoun by default for negatives
  const [negExcludePronouns, setNegExcludePronouns] = useState(new Set()); // Per-pronoun exclusion for negatives
  // Question generation state — per-tense
  const [questionProposals, setQuestionProposals] = useState({});
  const [questionPattern, setQuestionPattern] = useState({});
  const [qModal, setQModal] = useState(null); // { positiveLatin, verbName, tense, generating }
  const [qModalInput, setQModalInput] = useState('');
  const [qDropPronoun, setQDropPronoun] = useState(true); // Drop pronoun by default for questions
  const [qExcludePronouns, setQExcludePronouns] = useState(new Set()); // Per-pronoun exclusion for questions
  // Duplicate verb state
  const [dupModal, setDupModal] = useState(null); // { verb } or null
  const [dupNewName, setDupNewName] = useState('');
  const [dupLoading, setDupLoading] = useState(false);

  // Create empty verb (just adds name, user fills cards)
  const createNewVerb = (e) => {
    if (e) e.preventDefault();
    const name = newVerbName.trim().toLowerCase();
    if (!name) return;
    // Check if verb already exists in list
    if (verbs.some(v => v.base_verb === name)) {
      showNotification && showNotification(`Verb "${name}" already exists`, 'error');
      return;
    }
    // Add empty verb to local state so it appears immediately with 6 empty paste cards
    setVerbs(prev => [...prev, {
      base_verb: name,
      conjugations: [],
      total_conjugations: 0,
      positive_count: 0,
      negative_count: 0,
      question_count: 0,
      latin_count: 0,
      arabic_count: 0,
      by_tense: { past: { latin: 0, arabic: 0 }, present: { latin: 0, arabic: 0 }, future: { latin: 0, arabic: 0 } },
      by_tense_neg: { past: { latin: 0, arabic: 0 }, present: { latin: 0, arabic: 0 }, future: { latin: 0, arabic: 0 } },
      by_tense_q: { past: { latin: 0, arabic: 0 }, present: { latin: 0, arabic: 0 }, future: { latin: 0, arabic: 0 } },
      completeness: 'incomplete',
      missing: ['Past +', 'Present +', 'Future +', 'Past -', 'Present -', 'Future -', 'Past ?', 'Present ?', 'Future ?']
    }]);
    setNewVerbName('');
    setExpandedVerb(name);
    setPasteTab('past_pos');
    setPasteLatinText('');
    setPasteArabicText('');
    setAllTenseData({...emptyTenseData});
    setBaseForm({ latinM: '', latinF: '', arabicM: '', arabicF: '' });
    showNotification && showNotification(`"${name}" created — paste entries into the cards below`, 'success');
  };

  // Parse 10-line paste format into conjugation entries
  const parsePasteLines = (text, tense, script) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    const entries = [];
    for (const line of lines) {
      let english = '', darija = '';
      if (script === 'latin' && line.includes('=')) {
        const parts = line.split('=');
        english = parts[0].trim();
        darija = parts.slice(1).join('=').trim();
      } else if (script === 'arabic') {
        // Arabic lines: just the Darija text (with optional tags)
        darija = line;
      } else {
        continue;
      }
      // Detect tags for pronoun detection only — do NOT strip them, backend handles that
      const combined = darija.toUpperCase();
      let gender = null, number = null;
      if (combined.includes('(M)')) gender = 'male';
      else if (combined.includes('(F)')) gender = 'female';
      if (combined.includes('(S)')) number = 'singular';
      else if (combined.includes('(P)')) number = 'plural';
      // Detect pronoun from English (for latin) or position order (for arabic)
      let pronoun = '';
      const eLow = english.toLowerCase();
      if (eLow.startsWith('i ') || eLow === 'i') pronoun = 'i';
      else if (eLow.startsWith('you ')) pronoun = gender === 'female' ? 'you_sf' : number === 'plural' ? 'you_p' : 'you_sm';
      else if (eLow.startsWith('he ')) pronoun = 'he';
      else if (eLow.startsWith('she ')) pronoun = 'she';
      else if (eLow.startsWith('it ')) pronoun = 'it';
      else if (eLow.startsWith('we ')) pronoun = 'we';
      else if (eLow.startsWith('they ')) pronoun = 'they';
      // Send raw darija WITH tags — backend detects (M)(F)(S)(P) and strips them
      entries.push({
        sourceText: english.toLowerCase() || '',
        latinScript: script === 'latin' ? darija : '',
        translatedText: script === 'arabic' ? darija : '',
        tense,
        pronoun,
        gender,
        number
      });
    }
    return entries;
  };

  // Save pasted entries to backend
  const savePastedEntries = async (verbName) => {
    if (!pasteText.trim() || !pasteTarget) {
      console.warn('[PASTE SAVE] Aborted: pasteText empty or pasteTarget null', { pasteText: pasteText?.substring(0, 50), pasteTarget });
      return;
    }
    setPasteSaving(true);
    try {
      const { tense, script } = pasteTarget;
      const entries = parsePasteLines(pasteText, tense, script);
      console.log(`[PASTE SAVE] Parsed ${entries.length} entries for ${verbName} ${tense} ${script}`, entries.slice(0, 3));
      if (entries.length === 0) {
        showNotification && showNotification('No valid entries found — each Latin line needs "=" separator (e.g. I asked = Ana swalt)', 'error');
        setPasteSaving(false);
        return;
      }
      // Warning A: Line count check (expected 10: 1 base + 9 pronouns)
      if (entries.length < 10) {
        showNotification && showNotification(`Only ${entries.length} entries (expected 10: 1 base + 9 pronouns). Missing entries will be empty.`, 'warning');
      } else if (entries.length > 10) {
        showNotification && showNotification(`${entries.length} entries pasted (expected 10). Extra lines may cause issues.`, 'warning');
      }
      // If arabic script, we need to match with existing latin entries to get sourceText
      if (script === 'arabic') {
        // Find existing entries for this verb+tense to get English sourceText
        const existingVerb = verbs.find(v => v.base_verb === verbName);
        const existingConjs = existingVerb?.conjugations || [];
        const tenseConjs = existingConjs.filter(c => {
          const detected = detectTenseFromText(c.sourceText);
          return detected === tense;
        });
        // Warning B: Arabic vs Latin count mismatch
        if (tenseConjs.length > 0 && entries.length !== tenseConjs.length) {
          showNotification && showNotification(
            `${entries.length} Arabic lines but ${tenseConjs.length} Latin entries for ${tense}. ${
              entries.length > tenseConjs.length
                ? `${entries.length - tenseConjs.length} Arabic line(s) won't have a matching English source.`
                : `${tenseConjs.length - entries.length} Latin entry(s) won't get Arabic text.`
            }`, 'warning'
          );
        } else if (tenseConjs.length === 0) {
          showNotification && showNotification(`No Latin entries found for ${tense} — add Latin first so Arabic can map to English sources.`, 'error');
          setPasteSaving(false);
          return;
        }
        // Map arabic entries to existing sourceText by position
        for (let i = 0; i < entries.length && i < tenseConjs.length; i++) {
          if (!entries[i].sourceText && tenseConjs[i]) {
            entries[i].sourceText = tenseConjs[i].sourceText;
            entries[i].pronoun = entries[i].pronoun || tenseConjs[i].pronoun;
          }
        }
      }
      console.log(`[PASTE SAVE] Sending ${entries.length} entries to backend for ${verbName}...`);
      const res = await fetch(`${API}/admin/grammar/verbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ base_verb: verbName, conjugations: entries })
      });
      const data = await res.json();
      console.log('[PASTE SAVE] Backend response:', data);
      if (data.success) {
        const parts = [`${data.added_count} new`];
        if (data.updated_count) parts.push(`${data.updated_count} updated`);
        if (data.skipped_count) parts.push(`${data.skipped_count} skipped`);
        if (data.errors) parts.push(`${data.errors.length} errors`);
        showNotification && showNotification(`Saved: ${parts.join(', ')}`, data.added_count > 0 ? 'success' : 'info');
        if (data.duplicate_warning) {
          showNotification && showNotification(data.duplicate_warning, 'warning');
        }
        setPasteTarget(null);
        setPasteText('');
        // Keep expandedVerb so the user stays on the same verb
        await fetchVerbs(true);
      } else {
        console.error('[PASTE SAVE] Save failed:', data);
        showNotification && showNotification(data.detail || `Save failed: ${JSON.stringify(data)}`, 'error');
      }
    } catch (err) {
      console.error('[PASTE SAVE] Error:', err);
      showNotification && showNotification('Error saving: ' + err.message, 'error');
    }
    setPasteSaving(false);
  };

  // Helper: detect tense from sourceText
  const detectTenseFromText = (src) => {
    if (!src) return 'present';
    const words = src.toLowerCase().split(' ');
    if (words.includes('will')) return 'future';
    if (words.length >= 2) {
      const verb = words[1] && !['am','is','are','was','were','will','have','had'].includes(words[1]) ? words[1] : (words[2] || '');
      if (verb.endsWith('ed') || ['ate','drank','went','came','saw','knew','gave','took','slept','woke','ran','spoke','brought','bought','built','broke','bit','became'].includes(verb)) return 'past';
    }
    return 'present';
  };

  // Save both Latin + Arabic for a tense at once
  // Save base verb entry (M/F forms)
  const saveBaseEntry = async (verbName) => {
    if (!baseForm.latinM && !baseForm.latinF && !baseForm.arabicM && !baseForm.arabicF) return;
    setBaseSaving(true);
    try {
      const conjugations = [];
      // Male base entry
      if (baseForm.latinM || baseForm.arabicM) {
        conjugations.push({
          sourceText: verbName.toLowerCase(),
          latinScript: baseForm.latinM ? `${baseForm.latinM} (M)` : '',
          translatedText: baseForm.arabicM ? `${baseForm.arabicM} (M)` : '',
          tense: 'past',
          pronoun: 'base',
          gender: 'male',
          number: null
        });
      }
      // Female base entry
      if (baseForm.latinF || baseForm.arabicF) {
        conjugations.push({
          sourceText: verbName.toLowerCase(),
          latinScript: baseForm.latinF ? `${baseForm.latinF} (F)` : '',
          translatedText: baseForm.arabicF ? `${baseForm.arabicF} (F)` : '',
          tense: 'past',
          pronoun: 'base',
          gender: 'female',
          number: null
        });
      }
      if (conjugations.length === 0) {
        setBaseSaving(false);
        return;
      }
      const response = await fetch(`${API}/admin/grammar/verbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ base_verb: verbName, conjugations })
      });
      const data = await response.json();
      if (response.ok) {
        showNotification && showNotification(`Base verb saved for "${verbName}"`, 'success');
        fetchVerbs(true);
      } else {
        showNotification && showNotification(data.detail || 'Failed to save base entry', 'error');
      }
    } catch (err) {
      console.error('[BASE SAVE] Error:', err);
      showNotification && showNotification('Failed to save base entry', 'error');
    }
    setBaseSaving(false);
  };

  const saveTenseEntries = async (verbName, tense, latinOverride, arabicOverride) => {
    const latinSrc = latinOverride !== undefined ? latinOverride : pasteLatinText;
    const arabicSrc = arabicOverride !== undefined ? arabicOverride : pasteArabicText;
    if (!latinSrc.trim() && !arabicSrc.trim()) return;
    setPasteSaving(true);
    try {
      // Parse Latin lines (have English = Darija format)
      const latinLines = latinSrc.trim() ? latinSrc.split('\n').map(l => l.trim()).filter(l => l) : [];
      // Parse Arabic lines (just Darija text, one per line)
      const arabicLines = arabicSrc.trim() ? arabicSrc.split('\n').map(l => l.trim()).filter(l => l) : [];

      if (latinLines.length === 0 && arabicLines.length === 0) {
        showNotification && showNotification('Paste Latin (left) or Arabic (right) entries', 'error');
        setPasteSaving(false);
        return;
      }

      // Build entries from Latin lines (they have English sourceText)
      const entries = [];
      for (let i = 0; i < Math.max(latinLines.length, arabicLines.length); i++) {
        const latinLine = latinLines[i] || '';
        const arabicLine = arabicLines[i] || '';

        let english = '', latinDarija = '', arabicDarija = arabicLine;

        // Parse Latin line: "English = Darija (tags)"
        if (latinLine.includes('=')) {
          const parts = latinLine.split('=');
          english = parts[0].trim();
          latinDarija = parts.slice(1).join('=').trim();
        } else if (latinLine) {
          // No = sign, treat whole line as darija (for base verb or simple format)
          latinDarija = latinLine;
        }

        // Detect tags from both latin and arabic text for pronoun detection
        const combined = (latinDarija + ' ' + arabicDarija).toUpperCase();
        let gender = null, number = null;
        if (combined.includes('(M)')) gender = 'male';
        else if (combined.includes('(F)')) gender = 'female';
        if (combined.includes('(S)')) number = 'singular';
        else if (combined.includes('(P)')) number = 'plural';

        // Detect pronoun from English text
        let pronoun = '';
        const eLow = english.toLowerCase();
        if (eLow.startsWith('i ') || eLow === 'i') pronoun = 'i';
        else if (eLow.startsWith('you ')) pronoun = gender === 'female' ? 'you_sf' : number === 'plural' ? 'you_p' : 'you_sm';
        else if (eLow.startsWith('he ')) pronoun = 'he';
        else if (eLow.startsWith('she ')) pronoun = 'she';
        else if (eLow.startsWith('it ')) pronoun = 'it';
        else if (eLow.startsWith('we ')) pronoun = 'we';
        else if (eLow.startsWith('they ')) pronoun = 'they';

        // If no English from latin, try to get from existing entries (for arabic-only paste)
        if (!english && arabicDarija) {
          const existingVerb = verbs.find(v => v.base_verb === verbName);
          const existingConjs = existingVerb?.conjugations || [];
          const tenseConjs = existingConjs.filter(c => (c._detected_tense || c.tense || detectTenseFromText(c.sourceText)) === tense);
          if (tenseConjs[i]) {
            english = tenseConjs[i].sourceText || '';
            pronoun = pronoun || tenseConjs[i].pronoun || '';
          }
        }

        if (!english && !latinDarija && !arabicDarija) continue;

        entries.push({
          sourceText: english.toLowerCase() || '',
          latinScript: latinDarija,  // raw with tags — backend handles stripping
          translatedText: arabicDarija,  // raw with tags
          tense,
          pronoun,
          gender,
          number
        });
      }

      if (entries.length === 0) {
        showNotification && showNotification('No valid entries found', 'error');
        setPasteSaving(false);
        return;
      }

      // Warn about line count
      if (entries.length < 9) {
        showNotification && showNotification(`Only ${entries.length} entries (expected 9-10). Missing pronouns will be empty.`, 'warning');
      }
      if (latinLines.length > 0 && arabicLines.length > 0 && latinLines.length !== arabicLines.length) {
        showNotification && showNotification(`${latinLines.length} Latin lines vs ${arabicLines.length} Arabic lines — extra lines won't match.`, 'warning');
      }

      console.log(`[TENSE SAVE] Sending ${entries.length} entries for ${verbName} ${tense}`, entries.slice(0, 2));
      const res = await fetch(`${API}/admin/grammar/verbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ base_verb: verbName, conjugations: entries })
      });
      const data = await res.json();
      console.log('[TENSE SAVE] Response:', data);
      if (data.success) {
        const parts = [`${data.added_count} new`];
        if (data.updated_count) parts.push(`${data.updated_count} updated`);
        if (data.skipped_count) parts.push(`${data.skipped_count} skipped`);
        showNotification && showNotification(`${tense}: ${parts.join(', ')}`, data.added_count > 0 ? 'success' : 'info');
        if (data.duplicate_warning) {
          showNotification && showNotification(data.duplicate_warning, 'warning');
        }
        setPasteLatinText('');
        setPasteArabicText('');
        setAllTenseData(prev => ({...prev, [tense]: { latin: '', arabic: '' }}));
        await fetchVerbs(true);
      } else {
        showNotification && showNotification(data.detail || 'Save failed', 'error');
      }
    } catch (err) {
      console.error('[TENSE SAVE] Error:', err);
      showNotification && showNotification('Error: ' + err.message, 'error');
    }
    setPasteSaving(false);
  };

  // Reset (delete all entries) for a specific tense of a verb
  const resetTenseEntries = async (verbName, tense) => {
    const verb = verbs.find(v => v.base_verb === verbName);
    if (!verb) return;

    // Detect tense for each conjugation
    const detectT = (src) => {
      if (!src) return 'present';
      const words = src.toLowerCase().split(' ');
      if (words.includes('will')) return 'future';
      if (words.length >= 2) {
        const v = words[1] && !['am','is','are','was','were','will','have','had'].includes(words[1]) ? words[1] : (words[2] || '');
        if (v.endsWith('ed') || ['ate','drank','went','came','saw','knew','gave','took','slept','woke','ran','spoke','brought','bought','built','broke','bit','became'].includes(v)) return 'past';
      }
      return 'present';
    };

    const tenseEntries = verb.conjugations.filter(c => {
      const t = c._detected_tense || c.tense || detectT(c.sourceText);
      return t === tense;
    });

    if (tenseEntries.length === 0) {
      showNotification && showNotification(`No ${tense} entries to delete`, 'info');
      return;
    }

    const ok = await showConfirmDialog({
      title: `Reset ${tense}`,
      message: `Delete all ${tenseEntries.length} ${tense} entries for "${verbName}"? This cannot be undone.`,
      icon: '🗑️',
      confirmText: `Delete ${tenseEntries.length} entries`,
      confirmColor: 'red'
    });
    if (!ok) return;

    let deleted = 0;
    for (const conj of tenseEntries) {
      const conjId = conj.id || conj._id;
      if (!conjId) continue;
      try {
        const res = await fetch(`${API}/admin/translations/${conjId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (res.ok) deleted++;
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
    showNotification && showNotification(`Deleted ${deleted}/${tenseEntries.length} ${tense} entries`, deleted > 0 ? 'success' : 'error');
    await fetchVerbs(true);
  };

  // Start editing a conjugation
  const startEdit = (conj) => {
    setEditingConj(conj.id || conj._id);
    setEditForm({
      sourceText: conj.sourceText || '',
      latinScript: conj.latinScript || '',
      translatedText: conj.translatedText || '',
      gender: conj.gender || '',
      number: conj.number || ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingConj(null);
    setEditForm({ sourceText: '', latinScript: '', translatedText: '', gender: '', number: '' });
  };

  // Save edited conjugation
  const saveEdit = async (conjId) => {
    setSaving(true);
    try {
      // Backend expects form data, not JSON
      const formData = new FormData();
      formData.append('sourceText', editForm.sourceText);
      formData.append('translatedText', editForm.translatedText || ' '); // Required field
      formData.append('latinScript', editForm.latinScript || '');
      formData.append('status', 'verified');
      if (editForm.gender) formData.append('gender', editForm.gender);
      if (editForm.number) formData.append('number', editForm.number);

      const res = await fetch(`${API}/admin/translations/${conjId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        },
        body: formData
      });

      if (res.ok) {
        showNotification && showNotification('Conjugation updated!', 'success');
        cancelEdit();
        fetchVerbs(true); // Refresh list
      } else {
        const errData = await res.json();
        const errMsg = typeof errData.detail === 'string'
          ? errData.detail
          : (errData.message || errData.error || 'Failed to update');
        showNotification && showNotification(errMsg, 'error');
      }
    } catch (err) {
      console.error('Save error:', err);
      showNotification && showNotification('Error saving changes', 'error');
    }
    setSaving(false);
  };

  // Delete a conjugation
  const deleteConj = async (conjId, sourceText) => {
    const ok = await showConfirmDialog({
      title: 'Delete Entry',
      message: `Delete "${sourceText}"? This cannot be undone.`,
      icon: '🗑️',
      confirmText: 'Delete',
      confirmColor: 'red'
    });
    if (!ok) return;

    try {
      const res = await fetch(`${API}/admin/translations/${conjId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (res.ok) {
        showNotification && showNotification('Deleted successfully', 'success');
        fetchVerbs(true); // Refresh list
      } else {
        showNotification && showNotification('Failed to delete', 'error');
      }
    } catch (err) {
      console.error('Delete error:', err);
      showNotification && showNotification('Error deleting', 'error');
    }
  };

  // Delete all conjugations in a verb group
  const deleteVerbGroup = async (verb) => {
    const ok = await showConfirmDialog({
      title: 'Delete Verb Group',
      message: `Delete "${verb.base_verb.toUpperCase()}" and all ${verb.total_conjugations} forms? This cannot be undone.`,
      icon: '⚠️',
      confirmText: 'Delete All Forms',
      confirmColor: 'red'
    });
    if (!ok) return;
    let deleted = 0;
    for (const conj of verb.conjugations) {
      const conjId = conj.id || conj._id;
      if (!conjId) { console.warn('No ID for entry:', conj.sourceText); continue; }
      try {
        const res = await fetch(`${API}/admin/translations/${conjId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        if (res.ok) deleted++;
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
    showNotification && showNotification(`Deleted ${deleted} of ${verb.total_conjugations} forms`, deleted > 0 ? 'success' : 'error');
    fetchVerbs(true);
  };

  // Delete selected verbs
  const deleteSelectedVerbs = async () => {
    const selected = verbs.filter(v => selectedVerbs.has(v.base_verb));
    if (selected.length === 0) return;
    const totalForms = selected.reduce((sum, v) => sum + v.total_conjugations, 0);
    const ok = await showConfirmDialog({
      title: 'Delete Selected Verbs',
      message: `Delete ${selected.length} verb(s) with ${totalForms} total forms? This cannot be undone.`,
      icon: '⚠️',
      confirmText: `Delete ${selected.length} Verb(s)`,
      confirmColor: 'red'
    });
    if (!ok) return;
    let totalDeleted = 0;
    for (const verb of selected) {
      for (const conj of verb.conjugations) {
        const conjId = conj.id || conj._id;
        if (!conjId) continue;
        try {
          const res = await fetch(`${API}/admin/translations/${conjId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (res.ok) totalDeleted++;
        } catch (err) {
          console.error('Delete error:', err);
        }
      }
    }
    showNotification && showNotification(`Deleted ${totalDeleted} forms from ${selected.length} verb(s)`, totalDeleted > 0 ? 'success' : 'error');
    setSelectedVerbs(new Set());
    fetchVerbs(true);
  };

  // Toggle verb selection
  const toggleVerbSelection = (baseVerb) => {
    setSelectedVerbs(prev => {
      const next = new Set(prev);
      if (next.has(baseVerb)) next.delete(baseVerb);
      else next.add(baseVerb);
      return next;
    });
  };

  // Fetch verbs from API
  const fetchVerbs = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const url = searchTerm
        ? `${API}/admin/grammar/verbs?search=${encodeURIComponent(searchTerm)}`
        : `${API}/admin/grammar/verbs`;

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (res.ok) {
        const data = await res.json();
        setVerbs(data.verbs || []);
        setStats({
          total_verbs: data.total_verbs || 0,
          total_conjugations: data.total_conjugations || 0
        });
      } else {
        showNotification && showNotification('Failed to load verbs', 'error');
      }
    } catch (err) {
      console.error('Error fetching verbs:', err);
      showNotification && showNotification('Error loading verbs', 'error');
    }
    if (!silent) setLoading(false);
  };


  // Rename a verb (updates sourceText in all conjugation entries)
  const renameVerb = async (oldName, newName) => {
    if (!oldName || !newName || oldName === newName) return;
    setRenamingVerb(true);
    try {
      const res = await fetch(`${API}/admin/grammar/verbs/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ old_name: oldName, new_name: newName })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification && showNotification(`Renamed "${oldName}" → "${newName}" (${data.updated} entries updated)`, 'success');
        setEditingVerb(null);
        setEditVerbName('');
        fetchVerbs(true);
      } else {
        showNotification && showNotification(data.detail || 'Failed to rename verb', 'error');
      }
    } catch (err) {
      console.error('Error renaming verb:', err);
      showNotification && showNotification('Error renaming verb', 'error');
    }
    setRenamingVerb(false);
  };

  // ==================== DRAFT VERB FUNCTIONS ====================
  const fetchDraftVerbs = async () => {
    setDraftLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/draft-verbs`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDraftVerbs(data.verbs || []);
      }
    } catch (err) {
      console.error('Error fetching draft verbs:', err);
    }
    setDraftLoading(false);
  };

  const seedDraftVerbs = async () => {
    setSeedingDrafts(true);
    try {
      const res = await fetch(`${API}/admin/grammar/seed-draft-verbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification && showNotification(`Seeded ${data.verb_count} draft verbs (${data.total_inserted} entries)`, 'success');
        fetchDraftVerbs();
      } else {
        showNotification && showNotification(data.message || 'Failed to seed drafts', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error seeding draft verbs', 'error');
    }
    setSeedingDrafts(false);
  };

  const approveDraftVerb = async (verbGroup) => {
    try {
      const res = await fetch(`${API}/admin/grammar/approve-verb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ verb_group: verbGroup })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification && showNotification(`"${verbGroup}" approved and moved to View Verbs (${data.approved_count} entries)`, 'success');
        fetchDraftVerbs();
        fetchVerbs(true);
      } else {
        showNotification && showNotification(data.detail || 'Failed to approve verb', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error approving verb', 'error');
    }
  };

  const deleteDraftVerb = async (verbGroup) => {
    const confirmed = await showConfirmDialog({
      title: `Delete draft "${verbGroup}"?`,
      message: `This will remove all draft conjugation entries for "${verbGroup}". This cannot be undone.`,
      icon: '🗑️',
      confirmText: 'Delete',
      confirmColor: 'red'
    });
    if (!confirmed) return;
    try {
      const res = await fetch(`${API}/admin/grammar/draft-verbs/${encodeURIComponent(verbGroup)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification && showNotification(`Deleted draft "${verbGroup}" (${data.deleted_count} entries)`, 'success');
        fetchDraftVerbs();
      } else {
        showNotification && showNotification(data.detail || 'Failed to delete draft', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error deleting draft verb', 'error');
    }
  };

  const startDraftEdit = (verb, tense) => {
    // Initialize edit data with current values for all entries in this tense
    const editData = {};
    verb.conjugations.filter(c => c.tense === tense).forEach(c => {
      editData[c.id] = { latinScript: c.latinScript || '', translatedText: c.translatedText || '' };
    });
    setDraftEditData(editData);
    setDraftEditMode(verb.base_verb);
  };

  const saveDraftEdits = async () => {
    const entries = Object.entries(draftEditData).map(([id, data]) => ({
      id, latinScript: data.latinScript, translatedText: data.translatedText
    }));
    try {
      const res = await fetch(`${API}/admin/grammar/draft-verbs/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ entries })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification && showNotification(`Saved ${data.updated} entries`, 'success');
        setDraftEditMode(null);
        setDraftEditData({});
        fetchDraftVerbs();
      } else {
        showNotification && showNotification(data.detail || 'Failed to save', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error saving edits', 'error');
    }
  };

  useEffect(() => {
    fetchVerbs();
    fetchRules();
    fetchPatterns();
    fetchSubjunctives();
    fetchVerbObjectPatterns();
    fetchSkipChars();
    fetchDraftVerbs();
  }, []);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVerbs();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Test grammar translation
  const handleTest = async () => {
    if (!testText.trim()) return;

    setTestLoading(true);
    setTestResult(null);

    try {
      const res = await fetch(`${API}/admin/grammar/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ text: testText })
      });

      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      console.error('Test error:', err);
      setTestResult({ success: false, message: 'Error testing translation' });
    }

    setTestLoading(false);
  };

  // 9 Darija pronouns × 3 tenses = 27 forms per verb
  const DARIJA_PRONOUNS = [
    { key: 'i', label: 'I', tag: '' },
    { key: 'you_sm', label: 'You', tag: '(S) (M)' },
    { key: 'you_sf', label: 'You', tag: '(S) (F)' },
    { key: 'he', label: 'He', tag: '' },
    { key: 'she', label: 'She', tag: '' },
    { key: 'it', label: 'It', tag: '', autoFill: true },
    { key: 'we', label: 'We', tag: '' },
    { key: 'you_p', label: 'You', tag: '(P)' },
    { key: 'they', label: 'They', tag: '' }
  ];
  const DARIJA_TENSES = ['past', 'present', 'future'];

  // Generate conjugation template for new verb
  const generateConjugationTemplate = (baseVerb) => {
    const conjugations = [];

    DARIJA_TENSES.forEach(tense => {
      DARIJA_PRONOUNS.forEach(pronoun => {
        let verbForm = baseVerb;
        let prefix = '';

        if (tense === 'past') {
          verbForm = baseVerb.endsWith('e') ? baseVerb + 'd' : baseVerb + 'ed';
        } else if (tense === 'future') {
          prefix = 'will ';
        }

        const sourceText = `${pronoun.label.toLowerCase()} ${prefix}${verbForm}`.replace(/\s+/g, ' ').trim();

        conjugations.push({
          sourceText,
          latinScript: '',
          translatedText: '',
          tense,
          pronoun: pronoun.key,
          pronounLabel: pronoun.label,
          pronounTag: pronoun.tag,
          autoFill: pronoun.autoFill || false
        });
      });
    });

    return conjugations;
  };

  // Handle add new verb
  const handleAddVerb = async () => {
    if (!newVerbForm.base_verb.trim()) {
      showNotification && showNotification('Please enter a base verb', 'error');
      return;
    }

    // Send all conjugations including empty "It" rows — backend handles auto-fill
    // Filter out non-It empty rows, but keep It rows so backend can auto-fill from He/She
    const conjugationsToSend = newVerbForm.conjugations
      .filter(c => c.latinScript.trim() || (c.translatedText && c.translatedText.trim()) || c.pronoun === 'it')
      .map(c => ({
        sourceText: c.sourceText,
        latinScript: c.latinScript,
        translatedText: c.translatedText || '',
        tense: c.tense,
        pronoun: c.pronoun,
        // Tags like (S), (M), (F), (P) are parsed by backend — send as-is
      }));

    const nonItFilled = conjugationsToSend.filter(c => c.pronoun !== 'it' && (c.latinScript.trim() || c.translatedText.trim()));
    if (nonItFilled.length === 0) {
      showNotification && showNotification('Please fill in at least one conjugation', 'error');
      return;
    }

    try {
      const res = await fetch(`${API}/admin/grammar/verbs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          base_verb: newVerbForm.base_verb,
          conjugations: conjugationsToSend
        })
      });

      const data = await res.json();

      if (data.success) {
        showNotification && showNotification(`Added ${data.added_count} conjugations for "${newVerbForm.base_verb}"`, 'success');
        setNewVerbForm({ base_verb: '', conjugations: [] });
        fetchVerbs(true);
        setActiveSection('verbs');
      } else {
        showNotification && showNotification('Failed to add verb', 'error');
      }
    } catch (err) {
      console.error('Error adding verb:', err);
      showNotification && showNotification('Error adding verb', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-xl font-bold mb-2 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          Smart Grammar System
          <span className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded-full">Translation_DB</span>
        </h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage verb conjugations for smart sentence translation. Uses existing translations collection.
        </p>

        {/* Stats */}
        <div className="flex gap-4 flex-wrap">
          <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
            <span className="font-bold">{stats.total_verbs}</span> Unique Verbs
          </div>
          <div className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
            <span className="font-bold">{stats.total_conjugations}</span> Conjugations
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'verbs', label: 'View Verbs', icon: '📋' },
          { id: 'negation', label: 'Negative Forms', icon: '🚫', dot: '🟢' },
          { id: 'positive', label: 'Verb Manager (Positive)', icon: '📊', dot: '🟢' },
          { id: 'pronouns', label: 'Pronoun Mappings', icon: '👤', dot: '🟢' },
          { id: 'connectors', label: 'Smart Connectors', icon: '🔗', dot: '🟢' },
          { id: 'construction', label: 'Sentence Builder', icon: '🏗️', dot: '🟢' },
          { id: 'draft', label: `Verb Need Fix${draftVerbs.length > 0 ? ` (${draftVerbs.length})` : ''}`, icon: '🔧' },
          { id: 'rules', label: 'Grammar Rules', icon: '🧠' },
          { id: 'patterns', label: 'Sentence Patterns', icon: '📐' },
          { id: 'test', label: 'Test Grammar', icon: '🧪' },
          { id: 'cleanup', label: 'Cleanup', icon: '🧹' },
          { id: 'backup', label: 'Export / Import', icon: '💾' },
          { id: 'settings', label: 'Settings', icon: '⚙️' },
          { id: 'darija-rules', label: 'How Darija Works', icon: '📖' }
        ].map(section => (
          <button
            type="button"
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSection === section.id
                ? 'bg-emerald-600 text-white'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {section.icon} {section.label} {section.dot && <span className="ml-1">{section.dot}</span>}
          </button>
        ))}
      </div>

      {/* View Verbs Section */}
      {activeSection === 'verbs' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* How it works note */}
          <div className={`mb-4 p-3 rounded-lg border text-xs leading-relaxed ${isDarkMode ? 'bg-indigo-900/30 border-indigo-700/50 text-indigo-300' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}>
            <strong>How it works:</strong> Add a verb → fill in Past, Present, Future (positive) → Generate negatives & questions per tense.
            <span className={`mx-1 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`}>|</span>
            <strong>Duplicate:</strong> If two English verbs share the same Darija (e.g. "offer" & "invite" = 3rad), set up one verb fully, then click <strong>Duplicate</strong> to copy it with a new English name — Darija stays the same.
          </div>

          {/* Create New Verb + Search */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="New verb name (e.g., eat)"
              value={newVerbName}
              onChange={(e) => setNewVerbName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createNewVerb(); } }}
              className={`w-48 px-3 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-800'
              }`}
            />
            <button
              onClick={createNewVerb}
              disabled={!newVerbName.trim()}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                newVerbName.trim()
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              + Create
            </button>
            <input
              type="text"
              placeholder="Search verbs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`flex-1 px-4 py-2 rounded-lg border ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-800'
              }`}
            />
          </div>

          {/* Audit Button */}
          <div className="flex gap-2 mb-4 items-center">
            <button
              onClick={async () => {
                setAuditLoading(true);
                try {
                  const res = await fetch(`${API}/admin/grammar/verbs/audit`, {
                    headers: { 'Authorization': `Bearer ${adminToken}` }
                  });
                  if (!res.ok) throw new Error('Audit failed');
                  const data = await res.json();
                  setAuditData(data);
                  setShowAudit(true);
                } catch (err) {
                  alert('Audit failed: ' + err.message);
                } finally {
                  setAuditLoading(false);
                }
              }}
              disabled={auditLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                auditLoading
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-orange-600 text-white hover:bg-orange-700'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {auditLoading ? 'Scanning...' : 'Audit All Verbs'}
            </button>
            {auditData && (
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Last audit: {auditData.complete}/{auditData.total_verbs} complete, {auditData.total_missing_slots} missing slots
              </span>
            )}
            {showAudit && auditData && (
              <button
                onClick={() => setShowAudit(false)}
                className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
              >
                Hide Report
              </button>
            )}
          </div>

          {/* Audit Results Panel */}
          {showAudit && auditData && (
            <div className={`mb-4 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Verb Audit Report
              </h4>
              <div className="flex gap-4 mb-3 text-sm">
                <span className="px-3 py-1 rounded-full bg-emerald-600 text-white font-medium">
                  Complete: {auditData.complete}
                </span>
                <span className={`px-3 py-1 rounded-full font-medium ${auditData.incomplete > 0 ? 'bg-red-600 text-white' : 'bg-gray-500 text-white'}`}>
                  Incomplete: {auditData.incomplete}
                </span>
                <span className={`px-3 py-1 rounded-full font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-300 text-gray-700'}`}>
                  Total entries: {auditData.total_entries_in_db}
                </span>
                {auditData.entries_without_verb_group > 0 && (
                  <span className="px-3 py-1 rounded-full bg-yellow-600 text-white font-medium">
                    No verb_group: {auditData.entries_without_verb_group}
                  </span>
                )}
              </div>

              {/* Incomplete verbs list */}
              {auditData.verbs.filter(v => !v.is_complete).length > 0 && (
                <div>
                  <h5 className={`font-semibold mb-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    Verbs with Issues ({auditData.verbs.filter(v => !v.is_complete).length}):
                  </h5>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {auditData.verbs.filter(v => !v.is_complete).map((v, idx) => (
                      <div key={idx} className={`p-3 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {v.verb}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API}/admin/grammar/verbs/audit-approve`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                                    body: JSON.stringify({ base_verb: v.verb })
                                  });
                                  if (res.ok) {
                                    showNotification && showNotification(`"${v.verb}" approved`, 'success');
                                    setAuditData(prev => ({
                                      ...prev,
                                      complete: prev.complete + 1,
                                      incomplete: prev.incomplete - 1,
                                      verbs: prev.verbs.map(verb =>
                                        verb.verb === v.verb ? { ...verb, is_complete: true, audit_approved: true } : verb
                                      )
                                    }));
                                  }
                                } catch (err) {
                                  showNotification && showNotification('Error: ' + err.message, 'error');
                                }
                              }}
                            >
                              Looks Good
                            </button>
                            <span className={`text-sm font-mono px-2 py-0.5 rounded ${
                              v.total >= 54 ? (isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-700')
                                : (isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700')
                            }`}>
                              {v.total}/81 (missing {v.missing_count})
                            </span>
                          </div>
                        </div>
                        {/* Per form-tense breakdown */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {['positive', 'negative', 'question'].map(form => (
                            ['past', 'present', 'future'].map(tense => {
                              const count = v.per_form_tense[`${form}_${tense}`] || 0;
                              const label = `${form === 'positive' ? '+' : form === 'negative' ? '-' : '?'}${tense[0].toUpperCase()}`;
                              return (
                                <span key={`${form}_${tense}`} className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                  count === 9
                                    ? (isDarkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                                    : count > 0
                                      ? (isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-700')
                                      : (isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700')
                                }`}>
                                  {label}:{count}/9
                                </span>
                              );
                            })
                          ))}
                        </div>
                        {/* Problems details */}
                        {v.problems && v.problems.length > 0 && (
                          <div className={`mt-1 text-[11px] ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {v.problems.map((p, pi) => (
                              <div key={pi}>⚠ {p}</div>
                            ))}
                          </div>
                        )}
                        {/* Missing slots details — grouped by form+tense */}
                        {v.missing_slots && v.missing_slots.length > 0 && (
                          <div className={`mt-1 text-[11px] ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                            {(() => {
                              const grouped = {};
                              v.missing_slots.forEach(s => {
                                const label = `${s.form === 'positive' ? 'Positive' : s.form === 'negative' ? 'Negative' : 'Question'} ${s.tense}`;
                                if (!grouped[label]) grouped[label] = [];
                                grouped[label].push(s.pronoun);
                              });
                              return Object.entries(grouped).map(([label, pronouns]) => (
                                <div key={label}>Missing {label}: {pronouns.join(', ')} ({pronouns.length}/9)</div>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Complete verbs summary */}
              {auditData.complete > 0 && (
                <details className="mt-3">
                  <summary className={`cursor-pointer text-sm font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    Complete Verbs ({auditData.complete}) — click to expand
                  </summary>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {auditData.verbs.filter(v => v.is_complete).map((v, idx) => (
                      <span key={idx} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                        {v.verb} ({v.total})
                      </span>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Duplicate Detection */}
          {!loading && verbs.length > 0 && (() => {
            const names = verbs.map(v => v.base_verb);
            const dupes = [];
            for (let i = 0; i < names.length; i++) {
              for (let j = i + 1; j < names.length; j++) {
                const a = names[i], b = names[j];
                if (a + 'e' === b || b + 'e' === a || a + 's' === b || b + 's' === a) {
                  dupes.push([verbs[i], verbs[j]]);
                }
              }
            }
            if (dupes.length === 0) return null;
            return (
              <div className={`mb-4 p-3 rounded-lg border ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700 text-yellow-300' : 'bg-yellow-50 border-yellow-300 text-yellow-800'}`}>
                <p className="font-medium text-sm mb-2">Possible duplicates found ({dupes.length}):</p>
                {dupes.map(([a, b], i) => (
                  <p key={i} className="text-xs ml-2">
                    "{a.base_verb}" ({a.total_conjugations} forms) and "{b.base_verb}" ({b.total_conjugations} forms)
                  </p>
                ))}
              </div>
            );
          })()}

          {/* Selection toolbar */}
          {verbs.length > 0 && (
            <div className={`flex items-center justify-between mb-3 p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={verbs.length > 0 && selectedVerbs.size === verbs.length}
                  onChange={() => {
                    if (selectedVerbs.size === verbs.length) setSelectedVerbs(new Set());
                    else setSelectedVerbs(new Set(verbs.map(v => v.base_verb)));
                  }}
                  className="w-4 h-4 rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Select All ({verbs.length})
                </span>
              </label>
              {selectedVerbs.size > 0 && (
                <button
                  onClick={deleteSelectedVerbs}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete Selected ({selectedVerbs.size})
                </button>
              )}
            </div>
          )}

          {/* Verbs List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading verbs...</p>
            </div>
          ) : verbs.length === 0 ? (
            <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No verb conjugations found.
            </p>
          ) : (
            <div className="space-y-3">
              {verbs.map((verb, idx) => {
                // Light color palette for each verb (cycles through)
                const verbColors = [
                  { light: 'bg-emerald-50 border-emerald-200', dark: 'bg-emerald-900/15 border-emerald-800' },
                  { light: 'bg-blue-50 border-blue-200', dark: 'bg-blue-900/15 border-blue-800' },
                  { light: 'bg-purple-50 border-purple-200', dark: 'bg-purple-900/15 border-purple-800' },
                  { light: 'bg-amber-50 border-amber-200', dark: 'bg-amber-900/15 border-amber-800' },
                  { light: 'bg-rose-50 border-rose-200', dark: 'bg-rose-900/15 border-rose-800' },
                  { light: 'bg-cyan-50 border-cyan-200', dark: 'bg-cyan-900/15 border-cyan-800' },
                  { light: 'bg-orange-50 border-orange-200', dark: 'bg-orange-900/15 border-orange-800' },
                  { light: 'bg-indigo-50 border-indigo-200', dark: 'bg-indigo-900/15 border-indigo-800' },
                ];
                const vc = verbColors[idx % verbColors.length];
                return (
                <div
                  key={idx}
                  className={`rounded-lg border overflow-hidden ${isDarkMode ? vc.dark : vc.light}`}
                >
                  {/* Verb Header with Completeness */}
                  <div className={`w-full px-4 py-3 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedVerbs.has(verb.base_verb)}
                          onChange={(e) => { e.stopPropagation(); toggleVerbSelection(verb.base_verb); }}
                          className="w-4 h-4 rounded shrink-0"
                        />
                      <button
                        onClick={() => {
                          const isExpanding = expandedVerb !== verb.base_verb;
                          setExpandedVerb(isExpanding ? verb.base_verb : null);
                          setPasteLatinText(''); setPasteArabicText(''); setPasteTab('past_pos'); setAllTenseData({...emptyTenseData}); setNegativeProposals({}); setNegativePattern({}); setQuestionProposals({}); setQuestionPattern({});
                          // Initialize base form from existing base entries
                          if (isExpanding && verb.base_entries) {
                            const bEntries = verb.base_entries || [];
                            const maleE = bEntries.find(e => e.gender === 'male');
                            const femaleE = bEntries.find(e => e.gender === 'female');
                            const singleE = !maleE && !femaleE && bEntries.length > 0 ? bEntries[0] : null;
                            setBaseForm({
                              latinM: (maleE || singleE || {}).latinScript || '',
                              latinF: (femaleE || {}).latinScript || '',
                              arabicM: (maleE || singleE || {}).translatedText || '',
                              arabicF: (femaleE || {}).translatedText || ''
                            });
                          } else {
                            setBaseForm({ latinM: '', latinF: '', arabicM: '', arabicF: '' });
                          }
                        }}
                        className="flex items-center gap-3 flex-1"
                      >
                        {editingVerb === verb.base_verb ? (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editVerbName}
                              onChange={(e) => setEditVerbName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') renameVerb(verb.base_verb, editVerbName); if (e.key === 'Escape') { setEditingVerb(null); setEditVerbName(''); } }}
                              className={`px-2 py-0.5 rounded border text-sm font-bold w-32 ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                              autoFocus
                              disabled={renamingVerb}
                            />
                            <button
                              onClick={() => renameVerb(verb.base_verb, editVerbName)}
                              disabled={renamingVerb || !editVerbName.trim() || editVerbName.trim().toLowerCase() === verb.base_verb}
                              className="px-2 py-0.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {renamingVerb ? '...' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setEditingVerb(null); setEditVerbName(''); }}
                              className="px-2 py-0.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className={`font-bold text-lg ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              {verb.base_verb.toUpperCase()}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingVerb(verb.base_verb); setEditVerbName(verb.base_verb); }}
                              className={`p-0.5 rounded hover:bg-opacity-20 ${isDarkMode ? 'hover:bg-white text-gray-400 hover:text-emerald-400' : 'hover:bg-gray-300 text-gray-400 hover:text-emerald-600'}`}
                              title="Rename verb"
                            >
                              ✏️
                            </button>
                          </>
                        )}
                        {/* Completeness color badge */}
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                          verb.completeness === 'complete' ? (isDarkMode ? 'bg-green-800 text-green-300' : 'bg-green-100 text-green-700') :
                          verb.completeness === 'partial' ? (isDarkMode ? 'bg-yellow-800 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                          isDarkMode ? 'bg-red-800 text-red-300' : 'bg-red-100 text-red-700'
                        }`}>
                          {(verb.positive_count || 0) + (verb.negative_count || 0) + (verb.question_count || 0)}/81
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDarkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`} title="Positive">
                          +{verb.positive_count || 0}
                        </span>
                        {(verb.negative_count || 0) > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'}`} title="Negative">
                            -{verb.negative_count}
                          </span>
                        )}
                        {(verb.question_count || 0) > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`} title="Question">
                            Q{verb.question_count}
                          </span>
                        )}
                        <span className={`text-xl ${expandedVerb === verb.base_verb ? 'rotate-180' : ''} transition-transform`}>
                          ▼
                        </span>
                      </button>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const action = verb.locked ? 'unlock' : 'lock';
                          try {
                            const res = await fetch(`${API}/admin/grammar/verbs/${verb.base_verb}/${action}`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${adminToken}` }
                            });
                            if (!res.ok) throw new Error('Failed');
                            showNotification && showNotification(
                              `${verb.base_verb} ${action === 'lock' ? 'locked' : 'unlocked'}`,
                              'success'
                            );
                            fetchVerbs(true);
                          } catch (err) {
                            showNotification && showNotification(`Failed to ${action}: ${err.message}`, 'error');
                          }
                        }}
                        className={`px-2 py-1 text-xs rounded ml-2 ${verb.locked
                          ? (isDarkMode ? 'bg-amber-700 text-amber-200 hover:bg-amber-600' : 'bg-amber-100 text-amber-700 hover:bg-amber-200')
                          : (isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                        }`}
                        title={verb.locked ? `Unlock ${verb.base_verb}` : `Lock ${verb.base_verb} to prevent edits`}
                      >
                        {verb.locked ? 'Locked' : 'Lock'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDupModal(verb); setDupNewName(''); }}
                        className={`px-2 py-1 text-xs rounded ml-1 ${isDarkMode ? 'bg-indigo-700 text-indigo-200 hover:bg-indigo-600' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                        title={`Duplicate ${verb.base_verb} with new English name`}
                      >
                        Duplicate
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteVerbGroup(verb); }}
                        className={`px-2 py-1 text-xs rounded hover:bg-red-700 ml-1 ${verb.locked ? 'bg-red-400 cursor-not-allowed opacity-50' : 'bg-red-600'} text-white`}
                        title={verb.locked ? `${verb.base_verb} is locked` : `Delete all ${verb.total_conjugations} forms of ${verb.base_verb}`}
                        disabled={verb.locked}
                      >
                        Delete Group
                      </button>
                    </div>
                    {/* Per-tense breakdown (shown below verb name) */}
                    {verb.by_tense && (
                      <div className="flex flex-wrap gap-3 mt-1.5 ml-0">
                        {['past', 'present', 'future'].map(t => {
                          const td = verb.by_tense[t] || { latin: 0, arabic: 0 };
                          const nd = (verb.by_tense_neg || {})[t] || { latin: 0, arabic: 0 };
                          const qd = (verb.by_tense_q || {})[t] || { latin: 0, arabic: 0 };
                          const posComplete = td.latin >= 9 && td.arabic >= 9;
                          const posAny = td.latin > 0 || td.arabic > 0;
                          const negAny = nd.latin > 0 || nd.arabic > 0;
                          const qAny = qd.latin > 0 || qd.arabic > 0;
                          return (
                            <span key={t} className={`text-xs ${
                              posComplete && negAny && qAny ? (isDarkMode ? 'text-green-400' : 'text-green-600') :
                              posAny ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') :
                              isDarkMode ? 'text-red-400' : 'text-red-500'
                            }`}>
                              {posComplete ? '\u2705' : posAny ? '\u26A0\uFE0F' : '\u274C'} {t.charAt(0).toUpperCase() + t.slice(1)}: +{td.latin}/{td.arabic}
                              {negAny ? ` | -${nd.latin}/${nd.arabic}` : ''}
                              {qAny ? ` | ?${qd.latin}/${qd.arabic}` : ''}
                            </span>
                          );
                        })}
                        {verb.missing && verb.missing.length > 0 && (
                          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Missing: {verb.missing.join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Conjugations (Expanded) — 3×2 Grid: Past/Present/Future × Latin/Arabic */}
                  {expandedVerb === verb.base_verb && (
                    <div className={`px-4 pb-4 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'}`}>
                      {(() => {
                        // Helper to detect tense from sourceText
                        const detectTense = (src) => {
                          if (!src) return 'present';
                          const words = src.toLowerCase().split(' ');
                          if (words.includes('will')) return 'future';
                          if (words.length >= 2) {
                            const v = words[1] === 'will' || words[1] === 'am' || words[1] === 'is' || words[1] === 'are' ? (words[2] || '') : words[1];
                            if (v.endsWith('ed') || ['ate','drank','went','came','saw','knew','gave','took','slept','woke','ran','spoke','brought','bought','built','broke','bit','became'].includes(v)) return 'past';
                          }
                          return 'present';
                        };

                        // Extract pronoun label from sourceText
                        const getPronounLabel = (src) => {
                          if (!src) return '?';
                          const first = src.split(' ')[0].toLowerCase();
                          return first.charAt(0).toUpperCase() + first.slice(1);
                        };

                        // Group conjugations by tense AND form
                        const grouped = { past: [], present: [], future: [] };
                        const groupedNeg = { past: [], present: [], future: [] };
                        const groupedQ = { past: [], present: [], future: [] };
                        verb.conjugations.forEach(conj => {
                          const t = conj._detected_tense || conj.tense || detectTense(conj.sourceText);
                          const form = conj._form || conj.form || 'positive';
                          if (form === 'negative') {
                            if (groupedNeg[t]) groupedNeg[t].push(conj);
                            else groupedNeg.present.push(conj);
                          } else if (form === 'question') {
                            if (groupedQ[t]) groupedQ[t].push(conj);
                            else groupedQ.present.push(conj);
                          } else {
                            if (grouped[t]) grouped[t].push(conj);
                            else grouped.present.push(conj);
                          }
                        });

                        // Calculate totals
                        const posCount = verb.positive_count || verb.conjugations.filter(c => (c._form || c.form || 'positive') === 'positive').length;
                        const negCount = verb.negative_count || verb.conjugations.filter(c => (c._form || c.form) === 'negative').length;
                        const qCount = verb.question_count || verb.conjugations.filter(c => (c._form || c.form) === 'question').length;
                        const total = posCount + negCount + qCount;
                        const pct = Math.round((total / 81) * 100);

                        // Tense color map for columns
                        const tenseColors = {
                          past: { light: 'bg-amber-50 border-amber-200', dark: 'bg-amber-900/15 border-amber-700', header: isDarkMode ? 'text-amber-400' : 'text-amber-700', activeTab: isDarkMode ? 'bg-amber-700 text-white border-amber-500' : 'bg-amber-500 text-white border-amber-600' },
                          present: { light: 'bg-blue-50 border-blue-200', dark: 'bg-blue-900/15 border-blue-700', header: isDarkMode ? 'text-blue-400' : 'text-blue-700', activeTab: isDarkMode ? 'bg-blue-700 text-white border-blue-500' : 'bg-blue-500 text-white border-blue-600' },
                          future: { light: 'bg-purple-50 border-purple-200', dark: 'bg-purple-900/15 border-purple-700', header: isDarkMode ? 'text-purple-400' : 'text-purple-700', activeTab: isDarkMode ? 'bg-purple-700 text-white border-purple-500' : 'bg-purple-500 text-white border-purple-600' }
                        };

                        // Build gender/number tag display from stored data
                        const getGenderNumberTags = (conj) => {
                          const tags = [];
                          if (conj.number === 'singular') tags.push('(S)');
                          if (conj.number === 'plural') tags.push('(P)');
                          if (conj.gender === 'male') tags.push('(M)');
                          if (conj.gender === 'female') tags.push('(F)');
                          return tags.length > 0 ? ' ' + tags.join(' ') : '';
                        };

                        return (
                          <div className="mt-3 space-y-4">
                            {/* Progress Bar */}
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {total}/81
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  +{posCount} | -{negCount} | ?{qCount}
                                </span>
                              </div>
                              <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                                <div
                                  className={`h-2 rounded-full transition-all ${
                                    pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                            </div>

                            {/* Base Verb Section — M/F forms */}
                            {(() => {
                              const bEntries = verb.base_entries || [];
                              const maleEntry = bEntries.find(e => e.gender === 'male');
                              const femaleEntry = bEntries.find(e => e.gender === 'female');
                              // If only one entry with no gender, treat as male
                              const singleEntry = !maleEntry && !femaleEntry && bEntries.length > 0 ? bEntries[0] : null;
                              const hasBase = maleEntry || femaleEntry || singleEntry;
                              return (
                                <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-teal-900/20 border-teal-700' : 'bg-teal-50 border-teal-200'}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className={`text-xs font-bold uppercase ${isDarkMode ? 'text-teal-400' : 'text-teal-700'}`}>
                                      Base Verb
                                    </span>
                                    <button
                                      onClick={() => saveBaseEntry(verb.base_verb)}
                                      disabled={baseSaving}
                                      className="px-3 py-1 text-xs font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-all"
                                    >
                                      {baseSaving ? 'Saving...' : 'Save Base'}
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    {/* Latin row */}
                                    <div>
                                      <label className={`text-[10px] font-medium mb-1 block ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Latin</label>
                                      <div className="flex gap-2">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-1">
                                            <span className={`text-[9px] font-bold px-1 rounded ${isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>M</span>
                                            <input
                                              type="text"
                                              value={baseForm.latinM}
                                              onChange={(e) => setBaseForm(prev => ({ ...prev, latinM: e.target.value }))}
                                              placeholder={hasBase ? '' : 'e.g. twa7ach'}
                                              className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                            />
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-1">
                                            <span className={`text-[9px] font-bold px-1 rounded ${isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700'}`}>F</span>
                                            <input
                                              type="text"
                                              value={baseForm.latinF}
                                              onChange={(e) => setBaseForm(prev => ({ ...prev, latinF: e.target.value }))}
                                              placeholder={hasBase ? '' : 'e.g. twa7chat'}
                                              className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {/* Arabic row */}
                                    <div>
                                      <label className={`text-[10px] font-medium mb-1 block ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Arabic</label>
                                      <div className="flex gap-2">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-1">
                                            <span className={`text-[9px] font-bold px-1 rounded ${isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>M</span>
                                            <input
                                              type="text"
                                              value={baseForm.arabicM}
                                              onChange={(e) => setBaseForm(prev => ({ ...prev, arabicM: e.target.value }))}
                                              placeholder={hasBase ? '' : 'Arabic M'}
                                              dir="rtl"
                                              className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                            />
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center gap-1">
                                            <span className={`text-[9px] font-bold px-1 rounded ${isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700'}`}>F</span>
                                            <input
                                              type="text"
                                              value={baseForm.arabicF}
                                              onChange={(e) => setBaseForm(prev => ({ ...prev, arabicF: e.target.value }))}
                                              placeholder={hasBase ? '' : 'Arabic F'}
                                              dir="rtl"
                                              className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Tense Tabs: Past✅ | Present✅ | Future✅ | Past❌ | Present❌ | Future❌ */}
                            <div>
                              <div className="flex gap-1 mb-3 flex-wrap">
                                {/* Positive tabs — green inactive, yellow/amber active */}
                                {['past', 'present', 'future'].map(tense => {
                                  const tenseCount = grouped[tense].length;
                                  const tabKey = `${tense}_pos`;
                                  const isActive = pasteTab === tabKey;
                                  return (
                                    <button
                                      key={tabKey}
                                      onClick={() => { setPasteTab(tabKey); }}
                                      className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-bold uppercase transition-all border-b-3 ${
                                        isActive
                                          ? (isDarkMode ? 'bg-amber-600 text-white border-amber-400' : 'bg-amber-500 text-white border-amber-600')
                                          : (isDarkMode ? 'bg-emerald-900/40 text-emerald-400 border-emerald-700' : 'bg-emerald-100 text-emerald-700 border-emerald-300') + ' hover:opacity-80'
                                      }`}
                                    >
                                      {tense} <span className="text-[10px] font-normal ml-0.5">{tenseCount}</span>
                                    </button>
                                  );
                                })}
                                {/* Negative tabs — red inactive, orange active */}
                                {['past', 'present', 'future'].map(tense => {
                                  const tenseNegCount = groupedNeg[tense].length;
                                  const tabKey = `${tense}_neg`;
                                  const isActive = pasteTab === tabKey;
                                  return (
                                    <button
                                      key={tabKey}
                                      onClick={() => { setPasteTab(tabKey); }}
                                      className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-bold uppercase transition-all border-b-3 ${
                                        isActive
                                          ? (isDarkMode ? 'bg-orange-600 text-white border-orange-400' : 'bg-orange-500 text-white border-orange-600')
                                          : (isDarkMode ? 'bg-red-900/40 text-red-400 border-red-700' : 'bg-red-100 text-red-700 border-red-300') + ' hover:opacity-80'
                                      }`}
                                    >
                                      {tense} negative <span className="text-[10px] font-normal ml-0.5">{tenseNegCount}</span>
                                    </button>
                                  );
                                })}
                                {/* Question tabs — blue inactive, purple active */}
                                {['past', 'present', 'future'].map(tense => {
                                  const tenseQCount = groupedQ[tense].length;
                                  const tabKey = `${tense}_q`;
                                  const isActive = pasteTab === tabKey;
                                  return (
                                    <button
                                      key={tabKey}
                                      onClick={() => { setPasteTab(tabKey); }}
                                      className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-bold uppercase transition-all border-b-3 ${
                                        isActive
                                          ? (isDarkMode ? 'bg-purple-600 text-white border-purple-400' : 'bg-purple-500 text-white border-purple-600')
                                          : (isDarkMode ? 'bg-blue-900/40 text-blue-400 border-blue-700' : 'bg-blue-100 text-blue-700 border-blue-300') + ' hover:opacity-80'
                                      }`}
                                    >
                                      {tense} question <span className="text-[10px] font-normal ml-0.5">{tenseQCount}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Active tab content — POSITIVE tenses */}
                              {['past', 'present', 'future'].map(tense => pasteTab === `${tense}_pos` && (
                                <div key={`${tense}_pos`} className={`rounded-lg p-3 border ${isDarkMode ? tenseColors[tense].dark : tenseColors[tense].light}`}>
                                  {/* Existing entries table */}
                                  {grouped[tense].length > 0 && (
                                    <div className="mb-3 overflow-x-auto">
                                      <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        <thead>
                                          <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                            <th className="py-1.5 px-1 text-left w-6">#</th>
                                            <th className="py-1.5 px-2 text-left">English</th>
                                            <th className="py-1.5 px-2 text-left">Latin</th>
                                            <th className="py-1.5 px-2 text-right">Arabic</th>
                                            <th className="py-1.5 px-1 w-14"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {grouped[tense].map((conj, ci) => {
                                            const isEditing = editingConj === (conj.id || conj._id);
                                            const tags = getGenderNumberTags(conj);
                                            const genderBadge = conj.gender === 'male'
                                              ? { text: 'M', style: isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700' }
                                              : conj.gender === 'female'
                                              ? { text: 'F', style: isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700' }
                                              : null;
                                            const numberBadge = conj.number === 'singular'
                                              ? { text: 'S', style: isDarkMode ? 'bg-teal-800 text-teal-300' : 'bg-teal-100 text-teal-700' }
                                              : conj.number === 'plural'
                                              ? { text: 'P', style: isDarkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-700' }
                                              : null;

                                            if (isEditing) {
                                              return (
                                                <tr key={conj.id || conj._id} className={`${isDarkMode ? 'bg-gray-600' : 'bg-blue-50'}`}>
                                                  <td className="py-1.5 px-1 text-center font-mono">{ci + 1}</td>
                                                  <td className="py-1.5 px-1">
                                                    <input
                                                      type="text"
                                                      value={editForm.sourceText}
                                                      onChange={(e) => setEditForm({ ...editForm, sourceText: e.target.value })}
                                                      placeholder="English"
                                                      className={`w-full px-1.5 py-0.5 rounded border text-xs font-medium ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                                    />
                                                  </td>
                                                  <td className="py-1.5 px-1">
                                                    <input
                                                      type="text"
                                                      value={editForm.latinScript}
                                                      onChange={(e) => setEditForm({ ...editForm, latinScript: e.target.value })}
                                                      placeholder="Latin Darija"
                                                      className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                                    />
                                                  </td>
                                                  <td className="py-1.5 px-1">
                                                    <input
                                                      type="text"
                                                      value={editForm.translatedText}
                                                      onChange={(e) => setEditForm({ ...editForm, translatedText: e.target.value })}
                                                      placeholder="Arabic Script"
                                                      dir="rtl"
                                                      className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                                                    />
                                                  </td>
                                                  <td className="py-1.5 px-1">
                                                    <div className="flex flex-col gap-1">
                                                      <div className="flex gap-1">
                                                        <select value={editForm.gender} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })} className={`text-[10px] px-0.5 py-0.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}>
                                                          <option value="">--</option>
                                                          <option value="male">M</option>
                                                          <option value="female">F</option>
                                                        </select>
                                                        <select value={editForm.number} onChange={(e) => setEditForm({ ...editForm, number: e.target.value })} className={`text-[10px] px-0.5 py-0.5 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}>
                                                          <option value="">--</option>
                                                          <option value="singular">S</option>
                                                          <option value="plural">P</option>
                                                        </select>
                                                      </div>
                                                      <div className="flex gap-1">
                                                        <button onClick={() => saveEdit(conj.id || conj._id)} disabled={saving} className="px-1.5 py-0.5 text-[10px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
                                                          {saving ? '..' : '✓'}
                                                        </button>
                                                        <button onClick={cancelEdit} className={`px-1.5 py-0.5 text-[10px] rounded ${isDarkMode ? 'bg-gray-500 text-gray-200' : 'bg-gray-300 text-gray-700'}`}>
                                                          ✕
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            }

                                            return (
                                              <tr
                                                key={conj.id || conj._id}
                                                className={`group cursor-pointer border-b transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}
                                                onClick={() => startEdit(conj)}
                                              >
                                                <td className="py-1.5 px-1 text-center font-mono text-[10px]">{ci + 1}</td>
                                                <td className="py-1.5 px-2">
                                                  <span className="font-medium">{conj.sourceText}</span>
                                                  {numberBadge && <span className={`ml-1 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${numberBadge.style}`}>{numberBadge.text}</span>}
                                                  {genderBadge && <span className={`ml-0.5 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${genderBadge.style}`}>{genderBadge.text}</span>}
                                                </td>
                                                <td className={`py-1.5 px-2 ${conj.latinScript?.trim() ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') : (isDarkMode ? 'text-gray-600 italic' : 'text-gray-300 italic')}`}>
                                                  {conj.latinScript?.trim() || '\u2014'}
                                                </td>
                                                <td className={`py-1.5 px-2 text-right ${conj.translatedText?.trim() ? (isDarkMode ? 'text-blue-400 font-arabic' : 'text-blue-600 font-arabic') : (isDarkMode ? 'text-gray-600 italic' : 'text-gray-300 italic')}`} dir="rtl">
                                                  {conj.translatedText?.trim() || '\u2014'}
                                                </td>
                                                <td className="py-1.5 px-1">
                                                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button onClick={(e) => { e.stopPropagation(); startEdit(conj); }} className="p-1 rounded text-yellow-500 hover:bg-yellow-500/20" title="Edit"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></button>
                                                    <button onClick={(e) => { e.stopPropagation(); deleteConj(conj.id || conj._id, conj.sourceText); }} className="p-1 rounded text-red-500 hover:bg-red-500/20" title="Delete"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg></button>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Side-by-side paste: Latin (left) + Arabic (right) */}
                                  <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      Add/Update {tense.charAt(0).toUpperCase() + tense.slice(1)} Entries
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Latin (English = Darija)</label>
                                        <textarea
                                          value={allTenseData[tense]?.latin || ''}
                                          onChange={(e) => setAllTenseData(prev => ({...prev, [tense]: {...prev[tense], latin: e.target.value}}))}
                                          placeholder={`ask = sawal\nI asked = Ana swalt\nYou asked = Anta swalti (S) (M)\nYou asked = Anti swalti (S) (F)\nHe asked = Howa swal\nShe asked = Hiya swlat\nIt asked = Howa swal / Hiya swlat\nWe asked = Hena swalna\nYou asked = Ntoma swalto (P)\nThey asked = Homa swelo`}
                                          rows={8}
                                          className={`w-full px-2 py-1.5 rounded border text-xs font-mono ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-xs font-medium mb-1 block ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Arabic Script</label>
                                        <textarea
                                          value={allTenseData[tense]?.arabic || ''}
                                          onChange={(e) => setAllTenseData(prev => ({...prev, [tense]: {...prev[tense], arabic: e.target.value}}))}
                                          placeholder={`\u0633\u0648\u0644\n\u0623\u0646\u0627 \u0633\u0648\u0644\u062a\n\u0623\u0646\u062a \u0633\u0648\u0644\u062a\u064a\n\u0623\u0646\u062a\u064a \u0633\u0648\u0644\u062a\u064a\n\u0647\u0648 \u0633\u0648\u0644\n\u0647\u064a \u0633\u0648\u0644\u0627\u062a\n\u0647\u0648 \u0633\u0648\u0644 / \u0647\u064a \u0633\u0648\u0644\u0627\u062a\n\u062d\u0646\u0627 \u0633\u0648\u0644\u0646\u0627\n\u0623\u0646\u062a\u0648\u0645\u0627 \u0633\u0648\u0644\u062a\u0648\n\u0647\u0648\u0645\u0627 \u0633\u0648\u0644\u0648`}
                                          rows={8}
                                          dir="rtl"
                                          className={`w-full px-2 py-1.5 rounded border text-xs font-mono ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                        />
                                      </div>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                      <button
                                        onClick={() => saveTenseEntries(verb.base_verb, tense, allTenseData[tense]?.latin || '', allTenseData[tense]?.arabic || '')}
                                        disabled={pasteSaving || (!(allTenseData[tense]?.latin || '').trim() && !(allTenseData[tense]?.arabic || '').trim())}
                                        className="px-4 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all"
                                      >
                                        {pasteSaving ? 'Saving...' : `Save ${tense.charAt(0).toUpperCase() + tense.slice(1)}`}
                                      </button>
                                      {((allTenseData[tense]?.latin || '').trim() || (allTenseData[tense]?.arabic || '').trim()) && (
                                        <button
                                          onClick={() => setAllTenseData(prev => ({...prev, [tense]: { latin: '', arabic: '' }}))}
                                          className={`px-3 py-1.5 text-xs rounded-lg ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                                        >
                                          Clear
                                        </button>
                                      )}
                                      {grouped[tense].length > 0 && (
                                        <button
                                          onClick={() => resetTenseEntries(verb.base_verb, tense)}
                                          className={`ml-auto px-3 py-1.5 text-xs font-bold rounded-lg ${isDarkMode ? 'bg-red-800 text-red-200 hover:bg-red-700' : 'bg-red-100 text-red-600 hover:bg-red-200'} transition-all`}
                                        >
                                          Reset {tense.charAt(0).toUpperCase() + tense.slice(1)}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Active tab content — NEGATIVE tenses */}
                              {['past', 'present', 'future'].map(tense => pasteTab === `${tense}_neg` && (
                                <div key={`${tense}_neg`} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-red-900/15 border-red-800' : 'bg-red-50 border-red-200'}`}>
                                  {/* Show existing negative entries */}
                                  {groupedNeg[tense].length > 0 ? (
                                    <div className="mb-3 overflow-x-auto">
                                      <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        <thead>
                                          <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                            <th className="py-1.5 px-1 text-left w-6">#</th>
                                            <th className="py-1.5 px-2 text-left">English</th>
                                            <th className="py-1.5 px-2 text-left">Latin</th>
                                            <th className="py-1.5 px-2 text-right">Arabic</th>
                                            <th className="py-1.5 px-1 w-14"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {groupedNeg[tense].map((conj, ci) => {
                                            const isEditing = editingConj === (conj.id || conj._id);
                                            const negGenderBadge = conj.gender === 'male'
                                              ? { text: 'M', style: isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700' }
                                              : conj.gender === 'female'
                                              ? { text: 'F', style: isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700' }
                                              : null;
                                            const negNumberBadge = conj.number === 'singular'
                                              ? { text: 'S', style: isDarkMode ? 'bg-teal-800 text-teal-300' : 'bg-teal-100 text-teal-700' }
                                              : conj.number === 'plural'
                                              ? { text: 'P', style: isDarkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-700' }
                                              : null;
                                            if (isEditing) {
                                              return (
                                                <tr key={conj.id || conj._id} className={`${isDarkMode ? 'bg-gray-600' : 'bg-red-100'}`}>
                                                  <td className="py-1.5 px-1 text-center font-mono">{ci + 1}</td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.sourceText} onChange={(e) => setEditForm({ ...editForm, sourceText: e.target.value })} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.latinScript} onChange={(e) => setEditForm({ ...editForm, latinScript: e.target.value })} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.translatedText} onChange={(e) => setEditForm({ ...editForm, translatedText: e.target.value })} dir="rtl" className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1">
                                                    <div className="flex gap-1">
                                                      <button onClick={() => saveEdit(conj.id || conj._id)} disabled={saving} className="px-1.5 py-0.5 text-[10px] bg-emerald-600 text-white rounded">{saving ? '..' : '\u2713'}</button>
                                                      <button onClick={cancelEdit} className={`px-1.5 py-0.5 text-[10px] rounded ${isDarkMode ? 'bg-gray-500 text-gray-200' : 'bg-gray-300 text-gray-700'}`}>{'✕'}</button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                            return (
                                              <tr key={conj.id || conj._id} className={`group cursor-pointer border-b transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-red-100/50'}`} onClick={() => startEdit(conj)}>
                                                <td className="py-1.5 px-1 text-center font-mono text-[10px]">{ci + 1}</td>
                                                <td className="py-1.5 px-2">
                                                  <span className="font-medium">{conj.sourceText}</span>
                                                  {negNumberBadge && <span className={`ml-1 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${negNumberBadge.style}`}>{negNumberBadge.text}</span>}
                                                  {negGenderBadge && <span className={`ml-0.5 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${negGenderBadge.style}`}>{negGenderBadge.text}</span>}
                                                </td>
                                                <td className={`py-1.5 px-2 ${conj.latinScript?.trim() ? (isDarkMode ? 'text-red-400' : 'text-red-700') : 'italic text-gray-400'}`}>{conj.latinScript?.trim() || '\u2014'}</td>
                                                <td className={`py-1.5 px-2 text-right ${conj.translatedText?.trim() ? (isDarkMode ? 'text-red-400' : 'text-red-600') : 'italic text-gray-400'}`} dir="rtl">{conj.translatedText?.trim() || '\u2014'}</td>
                                                <td className="py-1.5 px-1">
                                                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                                                    <button onClick={(e) => { e.stopPropagation(); startEdit(conj); }} className="p-1 rounded text-yellow-500 hover:bg-yellow-500/20" title="Edit"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></button>
                                                    <button onClick={(e) => { e.stopPropagation(); deleteConj(conj.id || conj._id, conj.sourceText); }} className="p-1 rounded text-red-500 hover:bg-red-500/20" title="Delete"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg></button>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      <p className="text-sm">No negative entries for {tense} tense yet.</p>
                                      <p className="text-xs mt-1">Click "Generate" below to auto-create them.</p>
                                    </div>
                                  )}

                                  {/* Generate Negatives — per tense */}
                                  {posCount > 0 && (
                                    <div className={`mt-3 p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-red-700 bg-red-900/20' : 'border-red-300 bg-red-50'}`}>
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div>
                                          <span className={`text-sm font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Generate {tense.charAt(0).toUpperCase() + tense.slice(1)} Negatives</span>
                                          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Give one example and the system will auto-generate {tense} negative forms for review.
                                          </p>
                                        </div>
                                        <button
                                          onClick={() => {
                                            const exampleEntry = grouped[tense].find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.present?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.past?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.future?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped[tense]?.[0] ||
                                                                 grouped.present?.[0] || grouped.past?.[0] || grouped.future?.[0];
                                            if (!exampleEntry) {
                                              showNotification && showNotification('Add at least one positive entry first.', 'error');
                                              return;
                                            }
                                            const positiveLatin = exampleEntry.latinScript?.split(' ').slice(1).join(' ')?.trim() || exampleEntry.latinScript?.trim() || '';
                                            setNegModalInput('');
                                            setNegExcludePronouns(new Set());
                                            setNegModal({ positiveLatin, verbName: verb.base_verb, tense, generating: false });
                                          }}
                                          className="px-4 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                                        >
                                          Generate {tense.charAt(0).toUpperCase() + tense.slice(1)} Negatives
                                        </button>
                                        {groupedNeg[tense].length > 0 && (
                                          <button
                                            onClick={async () => {
                                              const ok = await showConfirmDialog({
                                                title: `Reset ${tense.charAt(0).toUpperCase() + tense.slice(1)} Negatives`,
                                                message: `Delete all ${groupedNeg[tense].length} saved ${tense} negative entries for "${verb.base_verb}"? You can regenerate them after.`,
                                                icon: '🔄',
                                                confirmText: `Delete ${groupedNeg[tense].length} entries`,
                                                confirmColor: 'red'
                                              });
                                              if (!ok) return;
                                              try {
                                                const res = await fetch(`${API}/admin/grammar/verbs/reset-form`, {
                                                  method: 'POST',
                                                  headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ base_verb: verb.base_verb, tense, form: 'negative' })
                                                });
                                                const result = await res.json();
                                                if (res.ok) {
                                                  showNotification && showNotification(`Reset: ${result.deleted_count} ${tense} negatives deleted`, 'success');
                                                  fetchVerbs(true);
                                                } else {
                                                  showNotification && showNotification(result.detail || 'Reset failed', 'error');
                                                }
                                              } catch (err) { showNotification && showNotification('Error: ' + err.message, 'error'); }
                                            }}
                                            className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                                          >
                                            Reset
                                          </button>
                                        )}
                                      </div>

                                      {/* Proposals review table — per tense */}
                                      {negativeProposals[tense]?.length > 0 && (
                                        <div className="mt-4">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                              Review Proposals ({negativeProposals[tense].length} entries) — Pattern: {negativePattern[tense]?.prefix || ''}...{negativePattern[tense]?.suffix || ''}
                                            </span>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => { setNegativeProposals(prev => { const n = {...prev}; delete n[tense]; return n; }); setNegativePattern(prev => { const n = {...prev}; delete n[tense]; return n; }); }}
                                                className={`px-3 py-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                                              >
                                                Discard
                                              </button>
                                              <button
                                                onClick={async () => {
                                                  try {
                                                    setPasteSaving(true);
                                                    const res = await fetch(`${API}/admin/grammar/verbs/save-negatives`, {
                                                      method: 'POST',
                                                      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                                      body: JSON.stringify({
                                                        base_verb: verb.base_verb,
                                                        conjugations: negativeProposals[tense]
                                                      })
                                                    });
                                                    const result = await res.json();
                                                    if (!res.ok) { showNotification && showNotification(result.detail || 'Save failed', 'error'); return; }
                                                    showNotification && showNotification(`Saved ${result.added_count} new + ${result.updated_count} updated negatives!`, 'success');
                                                    setNegativeProposals(prev => { const n = {...prev}; delete n[tense]; return n; });
                                                    setNegativePattern(prev => { const n = {...prev}; delete n[tense]; return n; });
                                                    fetchVerbs(true);
                                                  } catch (err) {
                                                    showNotification && showNotification('Error: ' + err.message, 'error');
                                                  } finally {
                                                    setPasteSaving(false);
                                                  }
                                                }}
                                                disabled={pasteSaving}
                                                className="px-4 py-1 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                                              >
                                                {pasteSaving ? 'Saving...' : `Save ${tense.charAt(0).toUpperCase() + tense.slice(1)} Negatives`}
                                              </button>
                                            </div>
                                          </div>
                                          <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                            <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                              <thead className="sticky top-0">
                                                <tr className={`border-b ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-red-50'}`}>
                                                  <th className="py-1.5 px-1 text-left w-6">#</th>
                                                  <th className="py-1.5 px-2 text-left">English (neg)</th>
                                                  <th className="py-1.5 px-2 text-left">Latin (neg)</th>
                                                  <th className="py-1.5 px-2 text-right">Arabic (neg)</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {negativeProposals[tense].map((prop, pi) => {
                                                  const propGenderBadge = prop.gender === 'male'
                                                    ? { text: 'M', style: isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700' }
                                                    : prop.gender === 'female'
                                                    ? { text: 'F', style: isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700' }
                                                    : null;
                                                  const propNumberBadge = prop.number === 'singular'
                                                    ? { text: 'S', style: isDarkMode ? 'bg-teal-800 text-teal-300' : 'bg-teal-100 text-teal-700' }
                                                    : prop.number === 'plural'
                                                    ? { text: 'P', style: isDarkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-700' }
                                                    : null;
                                                  return (
                                                    <tr key={pi} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                                      <td className="py-1.5 px-1 text-center font-mono text-[10px]">{pi + 1}</td>
                                                      <td className="py-1 px-1">
                                                        <div className="flex items-center gap-1">
                                                          <input type="text" value={prop.sourceText} onChange={(e) => { const updated = [...negativeProposals[tense]]; updated[pi] = { ...updated[pi], sourceText: e.target.value }; setNegativeProposals(prev => ({...prev, [tense]: updated})); }} className={`flex-1 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                                                          {propNumberBadge && <span className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${propNumberBadge.style}`}>{propNumberBadge.text}</span>}
                                                          {propGenderBadge && <span className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${propGenderBadge.style}`}>{propGenderBadge.text}</span>}
                                                        </div>
                                                      </td>
                                                      <td className="py-1 px-1">
                                                        <input type="text" value={prop.latinScript} onChange={(e) => { const updated = [...negativeProposals[tense]]; updated[pi] = { ...updated[pi], latinScript: e.target.value }; setNegativeProposals(prev => ({...prev, [tense]: updated})); }} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-red-300' : 'bg-white border-gray-300 text-red-700'}`} />
                                                      </td>
                                                      <td className="py-1 px-1">
                                                        <input type="text" value={prop.translatedText} onChange={(e) => { const updated = [...negativeProposals[tense]]; updated[pi] = { ...updated[pi], translatedText: e.target.value }; setNegativeProposals(prev => ({...prev, [tense]: updated})); }} dir="rtl" className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-red-300' : 'bg-white border-gray-300 text-red-600'}`} />
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}

                              {/* Active tab content — QUESTION tenses */}
                              {['past', 'present', 'future'].map(tense => pasteTab === `${tense}_q` && (
                                <div key={`${tense}_q`} className={`rounded-lg p-3 border ${isDarkMode ? 'bg-blue-900/15 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                                  {/* Show existing question entries */}
                                  {groupedQ[tense].length > 0 ? (
                                    <div className="mb-3 overflow-x-auto">
                                      <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        <thead>
                                          <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                                            <th className="py-1.5 px-1 text-left w-6">#</th>
                                            <th className="py-1.5 px-2 text-left">English</th>
                                            <th className="py-1.5 px-2 text-left">Latin</th>
                                            <th className="py-1.5 px-2 text-right">Arabic</th>
                                            <th className="py-1.5 px-1 w-14"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {groupedQ[tense].map((conj, ci) => {
                                            const isEditing = editingConj === (conj.id || conj._id);
                                            const qGenderBadge = conj.gender === 'male'
                                              ? { text: 'M', style: isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700' }
                                              : conj.gender === 'female'
                                              ? { text: 'F', style: isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700' }
                                              : null;
                                            const qNumberBadge = conj.number === 'singular'
                                              ? { text: 'S', style: isDarkMode ? 'bg-teal-800 text-teal-300' : 'bg-teal-100 text-teal-700' }
                                              : conj.number === 'plural'
                                              ? { text: 'P', style: isDarkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-700' }
                                              : null;
                                            if (isEditing) {
                                              return (
                                                <tr key={conj.id || conj._id} className={`${isDarkMode ? 'bg-gray-600' : 'bg-blue-100'}`}>
                                                  <td className="py-1.5 px-1 text-center font-mono">{ci + 1}</td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.sourceText} onChange={(e) => setEditForm({ ...editForm, sourceText: e.target.value })} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.latinScript} onChange={(e) => setEditForm({ ...editForm, latinScript: e.target.value })} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1"><input type="text" value={editForm.translatedText} onChange={(e) => setEditForm({ ...editForm, translatedText: e.target.value })} dir="rtl" className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}/></td>
                                                  <td className="py-1.5 px-1">
                                                    <div className="flex gap-1">
                                                      <button onClick={() => saveEdit(conj.id || conj._id)} disabled={saving} className="px-1.5 py-0.5 text-[10px] bg-emerald-600 text-white rounded">{saving ? '..' : '\u2713'}</button>
                                                      <button onClick={cancelEdit} className={`px-1.5 py-0.5 text-[10px] rounded ${isDarkMode ? 'bg-gray-500 text-gray-200' : 'bg-gray-300 text-gray-700'}`}>{'✕'}</button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                            return (
                                              <tr key={conj.id || conj._id} className={`group cursor-pointer border-b transition-colors ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-blue-100/50'}`} onClick={() => startEdit(conj)}>
                                                <td className="py-1.5 px-1 text-center font-mono text-[10px]">{ci + 1}</td>
                                                <td className="py-1.5 px-2">
                                                  <span className="font-medium">{conj.sourceText}</span>
                                                  {qNumberBadge && <span className={`ml-1 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${qNumberBadge.style}`}>{qNumberBadge.text}</span>}
                                                  {qGenderBadge && <span className={`ml-0.5 inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${qGenderBadge.style}`}>{qGenderBadge.text}</span>}
                                                </td>
                                                <td className={`py-1.5 px-2 ${conj.latinScript?.trim() ? (isDarkMode ? 'text-blue-400' : 'text-blue-700') : 'italic text-gray-400'}`}>{conj.latinScript?.trim() || '\u2014'}</td>
                                                <td className={`py-1.5 px-2 text-right ${conj.translatedText?.trim() ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : 'italic text-gray-400'}`} dir="rtl">{conj.translatedText?.trim() || '\u2014'}</td>
                                                <td className="py-1.5 px-1">
                                                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                                                    <button onClick={(e) => { e.stopPropagation(); startEdit(conj); }} className="p-1 rounded text-yellow-500 hover:bg-yellow-500/20" title="Edit"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></button>
                                                    <button onClick={(e) => { e.stopPropagation(); deleteConj(conj.id || conj._id, conj.sourceText); }} className="p-1 rounded text-red-500 hover:bg-red-500/20" title="Delete"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg></button>
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className={`text-center py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      <p className="text-sm">No question entries for {tense} tense yet.</p>
                                      <p className="text-xs mt-1">Click "Generate" below to auto-create them.</p>
                                    </div>
                                  )}

                                  {/* Generate Questions — per tense */}
                                  {posCount > 0 && (
                                    <div className={`mt-3 p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-300 bg-blue-50'}`}>
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div>
                                          <span className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Generate {tense.charAt(0).toUpperCase() + tense.slice(1)} Questions</span>
                                          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Give one example and the system will auto-generate {tense} question forms for review.
                                          </p>
                                        </div>
                                        <button
                                          onClick={() => {
                                            const exampleEntry = grouped[tense].find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.present?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.past?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped.future?.find(c => c.sourceText?.toLowerCase().startsWith('i ')) ||
                                                                 grouped[tense]?.[0] ||
                                                                 grouped.present?.[0] || grouped.past?.[0] || grouped.future?.[0];
                                            if (!exampleEntry) {
                                              showNotification && showNotification('Add at least one positive entry first.', 'error');
                                              return;
                                            }
                                            const positiveLatin = exampleEntry.latinScript?.split(' ').slice(1).join(' ')?.trim() || exampleEntry.latinScript?.trim() || '';
                                            setQModalInput('');
                                            setQExcludePronouns(new Set());
                                            setQModal({ positiveLatin, verbName: verb.base_verb, tense, generating: false });
                                          }}
                                          className="px-4 py-2 text-sm font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                                        >
                                          Generate {tense.charAt(0).toUpperCase() + tense.slice(1)} Questions
                                        </button>
                                        {groupedQ[tense].length > 0 && (
                                          <button
                                            onClick={async () => {
                                              const ok = await showConfirmDialog({
                                                title: `Reset ${tense.charAt(0).toUpperCase() + tense.slice(1)} Questions`,
                                                message: `Delete all ${groupedQ[tense].length} saved ${tense} question entries for "${verb.base_verb}"? You can regenerate them after.`,
                                                icon: '🔄',
                                                confirmText: `Delete ${groupedQ[tense].length} entries`,
                                                confirmColor: 'red'
                                              });
                                              if (!ok) return;
                                              try {
                                                const res = await fetch(`${API}/admin/grammar/verbs/reset-form`, {
                                                  method: 'POST',
                                                  headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ base_verb: verb.base_verb, tense, form: 'question' })
                                                });
                                                const result = await res.json();
                                                if (res.ok) {
                                                  showNotification && showNotification(`Reset: ${result.deleted_count} ${tense} questions deleted`, 'success');
                                                  fetchVerbs(true);
                                                } else {
                                                  showNotification && showNotification(result.detail || 'Reset failed', 'error');
                                                }
                                              } catch (err) { showNotification && showNotification('Error: ' + err.message, 'error'); }
                                            }}
                                            className={`px-3 py-2 text-xs font-medium rounded-lg transition-all ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                                          >
                                            Reset
                                          </button>
                                        )}
                                      </div>

                                      {/* Question proposals review table — per tense */}
                                      {questionProposals[tense]?.length > 0 && (
                                        <div className="mt-4">
                                          <div className="flex items-center justify-between mb-2">
                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                              Review Proposals ({questionProposals[tense].length} entries) — Pattern: {questionPattern[tense]?.prefix || ''}...{questionPattern[tense]?.suffix || ''}
                                            </span>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => { setQuestionProposals(prev => { const n = {...prev}; delete n[tense]; return n; }); setQuestionPattern(prev => { const n = {...prev}; delete n[tense]; return n; }); }}
                                                className={`px-3 py-1 text-xs rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                                              >
                                                Discard
                                              </button>
                                              <button
                                                onClick={async () => {
                                                  try {
                                                    setPasteSaving(true);
                                                    const res = await fetch(`${API}/admin/grammar/verbs/save-questions`, {
                                                      method: 'POST',
                                                      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                                                      body: JSON.stringify({
                                                        base_verb: verb.base_verb,
                                                        conjugations: questionProposals[tense]
                                                      })
                                                    });
                                                    const result = await res.json();
                                                    if (!res.ok) { showNotification && showNotification(result.detail || 'Save failed', 'error'); return; }
                                                    showNotification && showNotification(`Saved ${result.added_count} new + ${result.updated_count} updated questions!`, 'success');
                                                    setQuestionProposals(prev => { const n = {...prev}; delete n[tense]; return n; });
                                                    setQuestionPattern(prev => { const n = {...prev}; delete n[tense]; return n; });
                                                    fetchVerbs(true);
                                                  } catch (err) {
                                                    showNotification && showNotification('Error: ' + err.message, 'error');
                                                  } finally {
                                                    setPasteSaving(false);
                                                  }
                                                }}
                                                disabled={pasteSaving}
                                                className="px-4 py-1 text-xs font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                                              >
                                                {pasteSaving ? 'Saving...' : `Save ${tense.charAt(0).toUpperCase() + tense.slice(1)} Questions`}
                                              </button>
                                            </div>
                                          </div>
                                          <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                            <table className={`w-full text-xs border-collapse ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                              <thead className="sticky top-0">
                                                <tr className={`border-b ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-blue-50'}`}>
                                                  <th className="py-1.5 px-1 text-left w-6">#</th>
                                                  <th className="py-1.5 px-2 text-left">English (q)</th>
                                                  <th className="py-1.5 px-2 text-left">Latin (q)</th>
                                                  <th className="py-1.5 px-2 text-right">Arabic (q)</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {questionProposals[tense].map((prop, pi) => {
                                                  const propGenderBadge = prop.gender === 'male'
                                                    ? { text: 'M', style: isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-100 text-blue-700' }
                                                    : prop.gender === 'female'
                                                    ? { text: 'F', style: isDarkMode ? 'bg-pink-800 text-pink-300' : 'bg-pink-100 text-pink-700' }
                                                    : null;
                                                  const propNumberBadge = prop.number === 'singular'
                                                    ? { text: 'S', style: isDarkMode ? 'bg-teal-800 text-teal-300' : 'bg-teal-100 text-teal-700' }
                                                    : prop.number === 'plural'
                                                    ? { text: 'P', style: isDarkMode ? 'bg-orange-800 text-orange-300' : 'bg-orange-100 text-orange-700' }
                                                    : null;
                                                  return (
                                                    <tr key={pi} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                                      <td className="py-1.5 px-1 text-center font-mono text-[10px]">{pi + 1}</td>
                                                      <td className="py-1 px-1">
                                                        <div className="flex items-center gap-1">
                                                          <input type="text" value={prop.sourceText} onChange={(e) => { const updated = [...questionProposals[tense]]; updated[pi] = { ...updated[pi], sourceText: e.target.value }; setQuestionProposals(prev => ({...prev, [tense]: updated})); }} className={`flex-1 px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                                                          {propNumberBadge && <span className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${propNumberBadge.style}`}>{propNumberBadge.text}</span>}
                                                          {propGenderBadge && <span className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-bold ${propGenderBadge.style}`}>{propGenderBadge.text}</span>}
                                                        </div>
                                                      </td>
                                                      <td className="py-1 px-1">
                                                        <input type="text" value={prop.latinScript} onChange={(e) => { const updated = [...questionProposals[tense]]; updated[pi] = { ...updated[pi], latinScript: e.target.value }; setQuestionProposals(prev => ({...prev, [tense]: updated})); }} className={`w-full px-1.5 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-500 text-blue-300' : 'bg-white border-gray-300 text-blue-700'}`} />
                                                      </td>
                                                      <td className="py-1 px-1">
                                                        <input type="text" value={prop.translatedText} onChange={(e) => { const updated = [...questionProposals[tense]]; updated[pi] = { ...updated[pi], translatedText: e.target.value }; setQuestionProposals(prev => ({...prev, [tense]: updated})); }} dir="rtl" className={`w-full px-1.5 py-0.5 rounded border text-xs text-right ${isDarkMode ? 'bg-gray-700 border-gray-500 text-blue-300' : 'bg-white border-gray-300 text-blue-600'}`} />
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}

                              {/* Save All Tenses button */}
                              {(Object.values(allTenseData).some(t => (t.latin || '').trim() || (t.arabic || '').trim())) && (
                                <div className={`mt-3 p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-emerald-700 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50'}`}>
                                  <div className="flex items-center justify-between">
                                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {['past', 'present', 'future'].filter(t => (allTenseData[t]?.latin || '').trim() || (allTenseData[t]?.arabic || '').trim()).map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(' + ')} ready
                                    </div>
                                    <button
                                      onClick={async () => {
                                        setPasteSaving(true);
                                        for (const t of ['past', 'present', 'future']) {
                                          const latin = (allTenseData[t]?.latin || '').trim();
                                          const arabic = (allTenseData[t]?.arabic || '').trim();
                                          if (!latin && !arabic) continue;
                                          try {
                                            await saveTenseEntries(verb.base_verb, t, latin, arabic);
                                          } catch (err) {
                                            console.error(`Error saving ${t}:`, err);
                                          }
                                        }
                                        setAllTenseData({...emptyTenseData});
                                        setPasteSaving(false);
                                      }}
                                      disabled={pasteSaving}
                                      className="px-5 py-2 text-sm font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all"
                                    >
                                      {pasteSaving ? '⏳ Saving...' : '💾 Save All Tenses'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}
        </div>
      )}

      {/* Add New Verb Section — REMOVED: verb creation now inline in View Verbs */}
      {false && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Add New Verb Conjugations
          </h4>

          {/* Base Verb Input */}
          <div className="mb-4">
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Base Verb (English)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g., eat, drink, sleep"
                value={newVerbForm.base_verb}
                onChange={(e) => setNewVerbForm({ ...newVerbForm, base_verb: e.target.value.toLowerCase() })}
                className={`flex-1 px-4 py-2 rounded-lg border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300'
                }`}
              />
              <button
                onClick={() => {
                  if (newVerbForm.base_verb.trim()) {
                    setNewVerbForm({
                      ...newVerbForm,
                      conjugations: generateConjugationTemplate(newVerbForm.base_verb.trim())
                    });
                  }
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Generate Template
              </button>
            </div>
          </div>

          {/* Conjugation Form — 9 pronouns × 3 tenses = 27 rows */}
          {newVerbForm.conjugations.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Fill in the Darija translations (27 forms). "It" rows auto-fill from He/She.
                </p>
                <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                  {newVerbForm.conjugations.filter(c => c.latinScript.trim() || (c.translatedText && c.translatedText.trim())).length}/27 filled
                </span>
              </div>

              {/* Group by tense: Past, Present, Future */}
              {DARIJA_TENSES.map(tense => {
                const tenseConjugations = newVerbForm.conjugations.filter(c => c.tense === tense);
                const filledCount = tenseConjugations.filter(c => c.latinScript.trim() || (c.translatedText && c.translatedText.trim())).length;

                return (
                  <div key={tense} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h5 className={`font-bold capitalize ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        {tense.charAt(0).toUpperCase() + tense.slice(1)} Tense
                      </h5>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        filledCount === 9
                          ? 'bg-green-100 text-green-700'
                          : filledCount > 0
                            ? 'bg-yellow-100 text-yellow-700'
                            : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {filledCount}/9
                      </span>
                    </div>

                    {/* Column headers */}
                    <div className="hidden sm:grid sm:grid-cols-[180px_20px_1fr_1fr] gap-2 mb-2 px-1">
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>English</span>
                      <span></span>
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Latin Script</span>
                      <span className={`text-xs font-medium text-right ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Arabic Script</span>
                    </div>

                    <div className="grid gap-2">
                      {tenseConjugations.map((conj, idx) => {
                        const globalIdx = newVerbForm.conjugations.findIndex(c => c.sourceText === conj.sourceText && c.pronoun === conj.pronoun);
                        const isItRow = conj.autoFill;

                        // Auto-fill "It" from He/She within same tense
                        let autoFilledLatin = '';
                        let autoFilledArabic = '';
                        if (isItRow) {
                          const heConj = tenseConjugations.find(c => c.pronoun === 'he');
                          const sheConj = tenseConjugations.find(c => c.pronoun === 'she');
                          const heLatin = heConj?.latinScript?.trim() || '';
                          const sheLatin = sheConj?.latinScript?.trim() || '';
                          const heArabic = heConj?.translatedText?.trim() || '';
                          const sheArabic = sheConj?.translatedText?.trim() || '';
                          if (heLatin && sheLatin) autoFilledLatin = `${heLatin} / ${sheLatin}`;
                          else if (heLatin) autoFilledLatin = heLatin;
                          if (heArabic && sheArabic) autoFilledArabic = `${heArabic} / ${sheArabic}`;
                          else if (heArabic) autoFilledArabic = heArabic;
                        }

                        const displayLatin = conj.latinScript || (isItRow ? autoFilledLatin : '');
                        const displayArabic = conj.translatedText || (isItRow ? autoFilledArabic : '');

                        return (
                          <div key={idx} className={`flex flex-col sm:grid sm:grid-cols-[180px_20px_1fr_1fr] items-start sm:items-center gap-2 px-1 py-1 rounded ${
                            isItRow ? (isDarkMode ? 'bg-gray-600/30' : 'bg-blue-50/50') : ''
                          }`}>
                            <span className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {conj.sourceText}
                              {conj.pronounTag && (
                                <span className={`text-xs px-1 rounded ${
                                  conj.pronounTag.includes('M') ? 'bg-blue-100 text-blue-600' :
                                  conj.pronounTag.includes('F') ? 'bg-pink-100 text-pink-600' :
                                  'bg-purple-100 text-purple-600'
                                }`}>{conj.pronounTag}</span>
                              )}
                              {isItRow && <span className="text-xs text-gray-400" title="Auto-filled from He/She">auto</span>}
                            </span>
                            <span className="text-gray-500 hidden sm:inline text-center">→</span>
                            <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full sm:w-auto sm:contents">
                              <input
                                type="text"
                                placeholder={isItRow ? (autoFilledLatin || 'Auto from He/She') : 'Darija (Latin)'}
                                value={conj.latinScript}
                                onChange={(e) => {
                                  const updated = [...newVerbForm.conjugations];
                                  updated[globalIdx].latinScript = e.target.value;
                                  setNewVerbForm({ ...newVerbForm, conjugations: updated });
                                }}
                                className={`flex-1 px-3 py-1.5 rounded border text-sm ${
                                  isItRow && !conj.latinScript
                                    ? isDarkMode ? 'bg-gray-700/50 border-gray-500 text-gray-400 italic' : 'bg-gray-100 border-gray-200 text-gray-400 italic'
                                    : isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                }`}
                                style={isItRow && !conj.latinScript && autoFilledLatin ? { color: isDarkMode ? '#9ca3af' : '#9ca3af' } : {}}
                              />
                              <input
                                type="text"
                                placeholder={isItRow ? (autoFilledArabic || 'Auto from He/She') : 'الدارجة'}
                                value={conj.translatedText || ''}
                                onChange={(e) => {
                                  const updated = [...newVerbForm.conjugations];
                                  updated[globalIdx].translatedText = e.target.value;
                                  setNewVerbForm({ ...newVerbForm, conjugations: updated });
                                }}
                                className={`flex-1 px-3 py-1.5 rounded border text-sm text-right ${
                                  isItRow && !conj.translatedText
                                    ? isDarkMode ? 'bg-gray-700/50 border-gray-500 text-gray-400 italic' : 'bg-gray-100 border-gray-200 text-gray-400 italic'
                                    : isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                                }`}
                                dir="rtl"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <button
                onClick={handleAddVerb}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
              >
                Save Verb Conjugations ({newVerbForm.conjugations.filter(c => c.latinScript.trim() || (c.translatedText && c.translatedText.trim())).length} entries)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Negative Forms Section — self-contained in NegationGroupsAdmin.js */}
      {activeSection === 'negation' && (
        <NegationGroupsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
      )}

      {/* Positive Forms Section — self-contained in PositiveGroupsAdmin.js */}
      {activeSection === 'positive' && (
        <PositiveGroupsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} showNotification={showNotification} />
      )}

      {/* Pronoun Mappings Section — self-contained in PronounMappingsAdmin.js */}
      {activeSection === 'pronouns' && (
        <PronounMappingsAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} />
      )}

      {/* Smart Connectors Section — self-contained in ConnectorAdmin.js */}
      {activeSection === 'connectors' && (
        <ConnectorAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} />
      )}

      {/* Sentence Builder Section — self-contained in SentenceConstructionAdmin.js */}
      {activeSection === 'construction' && (
        <SentenceConstructionAdmin API={API} adminToken={adminToken} isDarkMode={isDarkMode} />
      )}

      {/* Verb Need Fix (Draft) Section */}
      {activeSection === 'draft' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Verb Need Fix ({draftVerbs.length} verbs)
            </h3>
            <div className="flex gap-2">
              {/* Seed button disabled — waiting for new verb list */}
              <button
                onClick={fetchDraftVerbs}
                className={`px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Refresh
              </button>
            </div>
          </div>

          <div className={`mb-4 p-3 rounded-lg border text-xs leading-relaxed ${isDarkMode ? 'bg-yellow-900/30 border-yellow-700/50 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
            <strong>How it works:</strong> Review each verb's Darija conjugation. Click any entry to edit Latin/Arabic. When a verb looks correct, click <strong>"Approve"</strong> to move it to View Verbs. Click <strong>"Delete"</strong> to remove verbs you don't want.
          </div>

          {draftLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading draft verbs...</p>
            </div>
          ) : draftVerbs.length === 0 ? (
            <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No draft verbs to review.
            </p>
          ) : (
            <div className="space-y-3">
              {draftVerbs.map((verb, idx) => {
                const draftColors = [
                  { light: 'bg-amber-50 border-amber-200', dark: 'bg-amber-900/15 border-amber-800' },
                  { light: 'bg-orange-50 border-orange-200', dark: 'bg-orange-900/15 border-orange-800' },
                  { light: 'bg-yellow-50 border-yellow-200', dark: 'bg-yellow-900/15 border-yellow-800' },
                  { light: 'bg-lime-50 border-lime-200', dark: 'bg-lime-900/15 border-lime-800' },
                ];
                const dc = draftColors[idx % draftColors.length];
                return (
                  <div key={idx} className={`rounded-lg border overflow-hidden ${isDarkMode ? dc.dark : dc.light}`}>
                    {/* Verb Header */}
                    <div className={`w-full px-4 py-3 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => { setDraftExpandedVerb(draftExpandedVerb === verb.base_verb ? null : verb.base_verb); setDraftActiveTense('past'); }}
                          className="flex items-center gap-3 flex-1"
                        >
                          <span className={`font-bold text-lg ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                            {verb.base_verb.toUpperCase()}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded font-bold ${isDarkMode ? 'bg-amber-800 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                            {verb.total_conjugations} entries
                          </span>
                          {verb.by_tense && ['past', 'present', 'future'].map(t => (
                            <span key={t} className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}: {(verb.by_tense[t] || {}).count || 0}
                            </span>
                          ))}
                          <span className={`text-xl ${draftExpandedVerb === verb.base_verb ? 'rotate-180' : ''} transition-transform`}>
                            ▼
                          </span>
                        </button>
                        <div className="flex gap-2 ml-2">
                          <button
                            onClick={() => approveDraftVerb(verb.base_verb)}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => deleteDraftVerb(verb.base_verb)}
                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Conjugation Table */}
                    {draftExpandedVerb === verb.base_verb && (
                      <div className={`px-4 pb-4 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'}`}>
                        {/* Tense tabs + Edit/Save buttons */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex gap-2">
                            {['past', 'present', 'future'].map(t => (
                              <button
                                key={t}
                                onClick={() => { setDraftActiveTense(t); if (draftEditMode === verb.base_verb) { setDraftEditMode(null); setDraftEditData({}); } }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                  draftActiveTense === t
                                    ? 'bg-amber-600 text-white'
                                    : isDarkMode
                                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            {draftEditMode === verb.base_verb ? (
                              <>
                                <button
                                  onClick={saveDraftEdits}
                                  className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                                >
                                  Save All
                                </button>
                                <button
                                  onClick={() => { setDraftEditMode(null); setDraftEditData({}); }}
                                  className="px-3 py-2 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => startDraftEdit(verb, draftActiveTense)}
                                className={`px-4 py-2 text-sm rounded-lg font-medium ${isDarkMode ? 'bg-blue-700 text-blue-100 hover:bg-blue-600' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                              >
                                Edit All
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Conjugation entries table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                <th className="text-left py-2 px-2 w-8">#</th>
                                <th className="text-left py-2 px-2">Pronoun</th>
                                <th className="text-left py-2 px-2">English</th>
                                <th className="text-left py-2 px-2">Latin (Darija)</th>
                                <th className="text-left py-2 px-2">Arabic (Darija)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {verb.conjugations
                                .filter(c => c.tense === draftActiveTense)
                                .sort((a, b) => {
                                  const order = ['base', 'i', 'you_sm', 'you_sf', 'he', 'she', 'it', 'we', 'you_p', 'they'];
                                  return order.indexOf(a.pronoun) - order.indexOf(b.pronoun);
                                })
                                .map((entry, i) => (
                                  <tr key={entry.id || i} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                                    <td className={`py-2 px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{i + 1}</td>
                                    <td className={`py-2 px-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {entry.pronoun === 'base' ? 'Base' : entry.pronoun === 'you_sm' ? 'You (S)(M)' : entry.pronoun === 'you_sf' ? 'You (S)(F)' : entry.pronoun === 'you_p' ? 'You (P)' : entry.pronoun.charAt(0).toUpperCase() + entry.pronoun.slice(1)}
                                    </td>
                                    <td className={`py-2 px-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                                      {entry.sourceText}
                                    </td>
                                    {draftEditMode === verb.base_verb && draftEditData[entry.id] ? (
                                      <>
                                        <td className="py-2 px-2">
                                          <input
                                            type="text"
                                            value={draftEditData[entry.id]?.latinScript || ''}
                                            onChange={(e) => setDraftEditData(prev => ({...prev, [entry.id]: {...prev[entry.id], latinScript: e.target.value}}))}
                                            className={`w-full px-2 py-1 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-700 border-gray-500 text-emerald-300' : 'bg-white border-gray-300 text-emerald-700'}`}
                                          />
                                        </td>
                                        <td className="py-2 px-2">
                                          <input
                                            type="text"
                                            value={draftEditData[entry.id]?.translatedText || ''}
                                            onChange={(e) => setDraftEditData(prev => ({...prev, [entry.id]: {...prev[entry.id], translatedText: e.target.value}}))}
                                            dir="rtl"
                                            className={`w-full px-2 py-1 rounded border text-sm font-mono ${isDarkMode ? 'bg-gray-700 border-gray-500 text-amber-300' : 'bg-white border-gray-300 text-amber-700'}`}
                                          />
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className={`py-2 px-2 font-mono ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                                          {entry.latinScript}
                                        </td>
                                        <td className={`py-2 px-2 font-mono text-right ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`} dir="rtl">
                                          {entry.translatedText}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Grammar Rules Section */}
      {activeSection === 'rules' && (
        <div className="space-y-6">
          {/* Existing Rules */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Active Grammar Rules ({grammarRules.length})
            </h4>
            {rulesLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
              </div>
            ) : grammarRules.length === 0 ? (
              <p className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                No grammar rules yet. Add your first rule below.
              </p>
            ) : (
              <div className="space-y-3">
                {grammarRules.map(rule => (
                  <div key={rule.id} className={`p-4 rounded-lg border ${
                    rule.enabled
                      ? isDarkMode ? 'bg-emerald-900/20 border-emerald-700' : 'bg-emerald-50 border-emerald-200'
                      : isDarkMode ? 'bg-gray-700/50 border-gray-600 opacity-60' : 'bg-gray-100 border-gray-300 opacity-60'
                  }`}>
                    {editingRule?.id === rule.id ? (
                      /* Inline Edit Mode */
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rule Name</label>
                            <input type="text" value={editingRule.name} onChange={(e) => setEditingRule({...editingRule, name: e.target.value})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                          </div>
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</label>
                            <select value={editingRule.type} onChange={(e) => setEditingRule({...editingRule, type: e.target.value})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                              <option value="skip_word">Skip Word</option>
                              <option value="replace_phrase">Replace Phrase</option>
                              <option value="conditional_skip">Conditional Skip</option>
                              <option value="rewrite">Rewrite (Regex)</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pattern</label>
                            <input type="text" value={editingRule.pattern} onChange={(e) => setEditingRule({...editingRule, pattern: e.target.value})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                          </div>
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Context</label>
                            <select value={editingRule.context || ''} onChange={(e) => setEditingRule({...editingRule, context: e.target.value})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                              <option value="">No context (always apply)</option>
                              <option value="before_verb">Before verb</option>
                              <option value="before_noun">Before noun</option>
                              <option value="after_pronoun">After pronoun</option>
                              <option value="at_start">At start of sentence</option>
                              <option value="at_end">At end of sentence</option>
                              <option value="standalone">Standalone word only</option>
                              <option value="between_words">Between any words</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Replacement</label>
                            <input type="text" value={editingRule.replacement || ''} onChange={(e) => setEditingRule({...editingRule, replacement: e.target.value})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                          </div>
                          <div>
                            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Priority</label>
                            <input type="number" value={editingRule.priority} onChange={(e) => setEditingRule({...editingRule, priority: parseInt(e.target.value) || 0})}
                              className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</label>
                          <input type="text" value={editingRule.description || ''} onChange={(e) => setEditingRule({...editingRule, description: e.target.value})}
                            className={`w-full px-2 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => updateRule(editingRule.id, { name: editingRule.name, type: editingRule.type, pattern: editingRule.pattern, context: editingRule.context, replacement: editingRule.replacement, priority: editingRule.priority, description: editingRule.description })}
                            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700">Save</button>
                          <button onClick={() => setEditingRule(null)}
                            className={`px-3 py-1.5 text-sm rounded ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-300 text-gray-700'}`}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              rule.type === 'skip_word' ? 'bg-yellow-500/20 text-yellow-600' :
                              rule.type === 'replace_phrase' ? 'bg-blue-500/20 text-blue-600' :
                              rule.type === 'conditional_skip' ? 'bg-orange-500/20 text-orange-600' :
                              rule.type === 'rewrite' ? 'bg-pink-500/20 text-pink-600' :
                              'bg-gray-500/20 text-gray-600'
                            }`}>{rule.type}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                              Priority: {rule.priority}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setEditingRule({...rule})}
                              className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'}`}>
                              Edit
                            </button>
                            <button onClick={() => updateRule(rule.id, { enabled: !rule.enabled })}
                              className={`px-2 py-1 text-xs rounded ${rule.enabled ? 'bg-emerald-600 text-white' : 'bg-gray-500 text-white'}`}>
                              {rule.enabled ? 'ON' : 'OFF'}
                            </button>
                            <button onClick={() => deleteRule(rule.id, rule.name)}
                              className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700">
                              Delete
                            </button>
                          </div>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Pattern: <code className={`px-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>{rule.pattern}</code>
                          {rule.context && <> | Context: <code className={`px-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>{rule.context}</code></>}
                          {rule.replacement && <> | Replace with: <code className={`px-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>{rule.replacement}</code></>}
                        </p>
                        {rule.description && (
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{rule.description}</p>
                        )}
                        {rule.examples && rule.examples.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {rule.examples.map((ex, i) => (
                              <span key={i} className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                                "{ex.input}" {ex.expected && <>→ "{ex.expected}"</>}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Rule */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Add New Grammar Rule
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Rule Name *</label>
                <input
                  type="text"
                  value={newRule.name}
                  onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                  placeholder="e.g., Skip 'to' before verbs"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Rule Type</label>
                <select
                  value={newRule.type}
                  onChange={(e) => setNewRule({...newRule, type: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="skip_word">Skip Word - Remove a word (e.g., "the", "a", "to")</option>
                  <option value="replace_phrase">Replace Phrase - Replace with Darija equivalent</option>
                  <option value="conditional_skip">Conditional Skip - Remove only in specific position</option>
                  <option value="rewrite">Rewrite (Regex) - Advanced pattern rewrite</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pattern * (word or phrase to match)</label>
                <input
                  type="text"
                  value={newRule.pattern}
                  onChange={(e) => setNewRule({...newRule, pattern: e.target.value})}
                  placeholder="e.g., to, the, is"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Context (when to apply)</label>
                <select
                  value={newRule.context}
                  onChange={(e) => setNewRule({...newRule, context: e.target.value})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Always apply (no context)</option>
                  <option value="before_verb">Before a verb (checks DB)</option>
                  <option value="before_noun">Before a noun (checks DB)</option>
                  <option value="after_pronoun">After a pronoun (I, you, he...)</option>
                  <option value="at_start">At start of sentence only</option>
                  <option value="at_end">At end of sentence only</option>
                  <option value="standalone">Standalone word only</option>
                  <option value="between_words">Between any words</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Replacement (for replace_phrase type)</label>
                <input
                  type="text"
                  value={newRule.replacement}
                  onChange={(e) => setNewRule({...newRule, replacement: e.target.value})}
                  placeholder="e.g., dyal, f"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Priority (higher = runs first)</label>
                <input
                  type="number"
                  value={newRule.priority}
                  onChange={(e) => setNewRule({...newRule, priority: parseInt(e.target.value) || 0})}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Description</label>
              <textarea
                value={newRule.description}
                onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                placeholder="Explain what this rule does and when it should apply..."
                rows={2}
                className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>

            {/* Examples */}
            <div className="mb-4">
              <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Examples (helps you remember what this rule does)</label>
              {newRule.examples.map((ex, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={ex.input}
                    onChange={(e) => {
                      const updated = [...newRule.examples];
                      updated[i].input = e.target.value;
                      setNewRule({...newRule, examples: updated});
                    }}
                    placeholder="Input: I love to go"
                    className={`flex-1 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                  <input
                    type="text"
                    value={ex.expected}
                    onChange={(e) => {
                      const updated = [...newRule.examples];
                      updated[i].expected = e.target.value;
                      setNewRule({...newRule, examples: updated});
                    }}
                    placeholder="Expected: kanhab namchi"
                    className={`flex-1 px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                  {i > 0 && (
                    <button
                      onClick={() => {
                        const updated = newRule.examples.filter((_, idx) => idx !== i);
                        setNewRule({...newRule, examples: updated});
                      }}
                      className="px-2 text-red-500 hover:text-red-700"
                    >X</button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setNewRule({...newRule, examples: [...newRule.examples, { input: '', expected: '' }]})}
                className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                + Add Example
              </button>
            </div>

            <button
              onClick={saveNewRule}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700"
            >
              Save Grammar Rule
            </button>
          </div>

          {/* How to Use Grammar Rules - Full Guide */}
          <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h4 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              How to Use Grammar Rules
            </h4>
            <div className={`text-sm space-y-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>

              <div>
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Step 1: Identify a Translation Problem</p>
                <p>Go to the <strong>Test Grammar</strong> tab and type a sentence. If the result is wrong, you need a grammar rule to fix it.</p>
                <p className="mt-1">Example: "I love to go" translates as "kanhab li sir" instead of "kanhab namchi"</p>
              </div>

              <div>
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Step 2: Create a Rule</p>
                <p>Use the <strong>Add New Grammar Rule</strong> form above. Choose the right rule type:</p>
              </div>

              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className={`font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>Rule Types Explained:</p>

                <div className="space-y-3">
                  <div>
                    <p className="font-bold">Skip Word</p>
                    <p>Removes a word. In Darija, some English words like "the", "a", "an", "to" don't exist.</p>
                    <p className={`mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Example: Skip "to" before verbs</p>
                    <p>Pattern: "to" | Context: "Before a verb" → "I love to go" becomes "I love go"</p>
                  </div>

                  <div>
                    <p className="font-bold">Replace Phrase</p>
                    <p>Replaces an English phrase with a Darija word before translation.</p>
                    <p className={`mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Example: Replace "of the" with "dyal"</p>
                    <p>Pattern: "of the" | Replacement: "dyal" → "house of the king" becomes "house dyal king"</p>
                  </div>

                  <div>
                    <p className="font-bold">Conditional Skip</p>
                    <p>Remove a word only in a specific position (start, end, after pronoun, etc.).</p>
                    <p className={`mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Example: Skip "am" after pronouns</p>
                    <p>Pattern: "am" | Context: "After pronoun" → "I am happy" becomes "I happy"</p>
                  </div>

                  <div>
                    <p className="font-bold">Rewrite (Regex)</p>
                    <p>Advanced: Use regex patterns to rewrite text. Pattern is a regex, replacement can use \1, \2 etc.</p>
                    <p className={`mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Example: "do not" → "don't" or restructure sentence</p>
                  </div>
                </div>

                <div className={`mt-3 p-2 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-blue-50'}`}>
                  <p className={`font-bold text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Smart Features (automatic):</p>
                  <ul className="list-disc ml-4 mt-1 space-y-0.5 text-xs">
                    <li>Compound sentences: "if/when/and/but/because" splits and translates each part</li>
                    <li>No pronoun needed: works with any multi-word phrase, not just "I go" etc.</li>
                    <li>Longest match: tries full phrase first, then shorter matches</li>
                    <li>Reverse match: if start doesn't match, tries matching from the end</li>
                  </ul>
                </div>
              </div>

              <div>
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Step 3: Test Your Rule</p>
                <p>After adding a rule, go to <strong>Test Grammar</strong> tab and test the same sentence again. You'll see:</p>
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li><strong>Pipeline Steps</strong> - Shows exactly what each rule did step by step</li>
                  <li><strong>Rules Applied</strong> - Which rules fired and how they changed the text</li>
                  <li><strong>Final Translation</strong> - The result after all rules are applied</li>
                </ul>
              </div>

              <div>
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Step 4: Fine-tune</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Priority:</strong> Higher number = runs first. If two rules conflict, the higher priority one wins.</li>
                  <li><strong>ON/OFF toggle:</strong> Disable a rule without deleting it to test the effect.</li>
                  <li><strong>Examples:</strong> Add example sentences when creating rules so you remember what they're for.</li>
                </ul>
              </div>

              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Quick Start Tips:</p>
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li>Start with <strong>View Verbs</strong> tab to add verb conjugations (i eat, you eat, he eats, etc.)</li>
                  <li>Then add <strong>Grammar Rules</strong> to fix common patterns (skip "to", replace "of the", etc.)</li>
                  <li>Always <strong>test</strong> after adding rules to make sure they work correctly</li>
                  <li>Rules apply to ALL translations automatically - not just the test tab</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sentence Patterns Section */}
      {activeSection === 'patterns' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Sentence Patterns ({sentencePatterns.length})
              </h4>
              <button
                onClick={() => setShowPatternHelp(prev => !prev)}
                className={`text-xs px-2 py-1 rounded-full font-bold ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-700'}`}
                title="How Sentence Patterns work"
              >?</button>
            </div>
            <button onClick={fetchPatterns} className="text-sm px-3 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
              Refresh
            </button>
          </div>

          {showPatternHelp && (
            <div className={`mb-4 p-4 rounded-lg border text-sm leading-relaxed ${isDarkMode ? 'bg-blue-900/30 border-blue-700 text-blue-200' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
              <div className="flex justify-between items-start mb-2">
                <h5 className="font-bold text-base">How Sentence Patterns Work</h5>
                <button onClick={() => setShowPatternHelp(false)} className="text-xs px-2 py-1 rounded bg-blue-200 text-blue-700 hover:bg-blue-300">Close</button>
              </div>

              <p className="mb-3">Sentence Patterns translate <strong>full sentences</strong> by breaking them into parts. There are 3 systems that work together:</p>

              <div className="mb-3">
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>1. Sentence Patterns (this list)</p>
                <p className="mb-1">These are the <strong>trigger words</strong> that start a sentence type:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>WH-Questions:</strong> "where is the store?" — "where" triggers, looks up "store" in DB</li>
                  <li><strong>Modal + Infinitive:</strong> "i want to eat" — "want to" triggers, looks up "eat" in Verb Subjunctive</li>
                  <li><strong>There is/are:</strong> "there is food" — "there is" triggers, looks up "food" in DB</li>
                  <li><strong>Phrase Combo:</strong> "let me" / "give me" — trigger translates, rest from DB</li>
                </ul>
                <p className="mt-1 opacity-75">Modal patterns store conjugation tables: i=bghit, you=bghiti, he=bgha, she=bghat...</p>
              </div>

              <div className="mb-3">
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>2. Verb + Object Patterns</p>
                <p className="mb-1">Handles <strong>"subject + verb + object pronoun"</strong> with automatic negative & question:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Positive:</strong> "i love her" → kanhabha / كنحبها</li>
                  <li><strong>Negative:</strong> "i don't love her" → makanhabhash / ماكنحبهاش</li>
                  <li><strong>Question:</strong> "does he love me?" → wash kayhabni / واش كيحبني</li>
                </ul>
                <p className="mt-1 opacity-75">Object suffixes: me=-ni, you=-ak, him=-o, her=-ha, us=-na, them=-hom</p>
              </div>

              <div className="mb-3">
                <p className={`font-bold mb-1 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>3. Verb Subjunctive Forms</p>
                <p className="mb-1">Stores the <strong>verb part after "to"</strong> in modal sentences:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>"i want to <strong>eat</strong>" — Modal gives "bghit", Subjunctive gives "nakol" → <strong>bghit nakol</strong></li>
                  <li>"she needs to <strong>sleep</strong>" — Modal gives "khasha", Subjunctive gives "t3ass" → <strong>khasha t3ass</strong></li>
                </ul>
              </div>

              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <p className="font-bold mb-1">Buttons Guide:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Batch Auto-Generate</strong> — Creates patterns for ALL verbs from your DB at once</li>
                  <li><strong>Auto-Generate (single)</strong> — Type one verb name, creates pattern from DB data</li>
                  <li><strong>Edit</strong> — Fix wrong conjugations manually (click Edit → change → Save)</li>
                  <li><strong>Add New Sentence Pattern</strong> — Manually add a new trigger (WH-word, modal, phrase)</li>
                  <li><strong>Add Verb Subjunctive</strong> — Manually add one verb's subjunctive forms</li>
                  <li><strong>Test</strong> — Type any sentence at top to test if it matches a pattern</li>
                </ul>
              </div>
            </div>
          )}

          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Sentence patterns handle structural translations: WH-questions ("where is X?"), modals ("I want to X"), "there is/are", and phrase combos.
            They combine trigger translations with DB lookups for the remaining words.
          </p>

          {/* Test Pattern */}
          <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Test a sentence:</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={patternTestText}
                onChange={e => setPatternTestText(e.target.value)}
                placeholder="e.g. where is the store"
                onKeyDown={e => e.key === 'Enter' && handleTestPattern()}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              />
              <button
                onClick={handleTestPattern}
                disabled={patternTestLoading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {patternTestLoading ? 'Testing...' : 'Test'}
              </button>
            </div>
            {patternTestResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${patternTestResult.success ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200') : (isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200')}`}>
                {patternTestResult.success ? (
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>Match found!</p>
                    <p>Latin: <strong>{patternTestResult.latin}</strong></p>
                    <p>Arabic: <strong>{patternTestResult.arabic}</strong></p>
                    {patternTestResult.steps?.map((s, i) => (
                      <p key={i} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{s.step}: {s.detail}</p>
                    ))}
                  </div>
                ) : (
                  <p className={isDarkMode ? 'text-red-300' : 'text-red-600'}>{patternTestResult.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Existing Patterns List */}
          {patternsLoading ? (
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading patterns...</p>
          ) : sentencePatterns.length === 0 ? (
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No sentence patterns yet. Restart backend to seed defaults, or add one below.</p>
          ) : (
            <div className="space-y-3 mb-6">
              {sentencePatterns.map(pat => (
                <div key={pat.id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} ${!pat.enabled ? 'opacity-50' : ''}`}>
                  {editingPattern?.id === pat.id ? (
                    /* Edit mode */
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input value={editingPattern.name} onChange={e => setEditingPattern({...editingPattern, name: e.target.value})} placeholder="Name" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                        <select value={editingPattern.type} onChange={e => setEditingPattern({...editingPattern, type: e.target.value})} className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}>
                          <option value="wh_question">WH-Question</option>
                          <option value="modal_infinitive">Modal + Infinitive</option>
                          <option value="there_is">There is/are</option>
                          <option value="phrase_combo">Phrase Combo</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <input value={editingPattern.trigger} onChange={e => setEditingPattern({...editingPattern, trigger: e.target.value})} placeholder="Trigger (e.g. where)" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                        {editingPattern.type !== 'modal_infinitive' && <>
                          <input value={editingPattern.latin_translation} onChange={e => setEditingPattern({...editingPattern, latin_translation: e.target.value})} placeholder="Latin translation (e.g. fin)" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                          <input value={editingPattern.arabic_translation} onChange={e => setEditingPattern({...editingPattern, arabic_translation: e.target.value})} placeholder="Arabic translation" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                        </>}
                      </div>
                      {/* Modal conjugation table */}
                      {editingPattern.type === 'modal_infinitive' && (
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-purple-50'}`}>
                          <label className={`text-xs font-medium mb-2 block ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Modal Conjugations (per pronoun):</label>
                          {['i', 'you', 'he', 'she', 'we', 'they'].map(pron => (
                            <div key={pron} className="grid grid-cols-3 gap-2 mb-1">
                              <span className={`text-xs py-1 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{pron.charAt(0).toUpperCase() + pron.slice(1)}:</span>
                              <input
                                value={editingPattern.conjugations?.[pron]?.latin || ''}
                                onChange={e => setEditingPattern({...editingPattern, conjugations: {...(editingPattern.conjugations || {}), [pron]: {...(editingPattern.conjugations?.[pron] || {}), latin: e.target.value}}})}
                                placeholder="Latin"
                                className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-500 border-gray-400 text-white' : 'bg-white border-gray-300'}`}
                              />
                              <input
                                value={editingPattern.conjugations?.[pron]?.arabic || ''}
                                onChange={e => setEditingPattern({...editingPattern, conjugations: {...(editingPattern.conjugations || {}), [pron]: {...(editingPattern.conjugations?.[pron] || {}), arabic: e.target.value}}})}
                                placeholder="Arabic"
                                className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-500 border-gray-400 text-white' : 'bg-white border-gray-300'}`}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <input value={editingPattern.description} onChange={e => setEditingPattern({...editingPattern, description: e.target.value})} placeholder="Description" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                        <input type="number" value={editingPattern.priority} onChange={e => setEditingPattern({...editingPattern, priority: parseInt(e.target.value) || 0})} placeholder="Priority" className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => updatePattern(pat.id, editingPattern)} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700">Save</button>
                        <button onClick={() => setEditingPattern(null)} className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div>
                      <div className="flex justify-between items-start">
                        <div>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{pat.name}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                            pat.type === 'wh_question' ? 'bg-blue-100 text-blue-700' :
                            pat.type === 'modal_infinitive' ? 'bg-purple-100 text-purple-700' :
                            pat.type === 'there_is' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {pat.type === 'wh_question' ? 'WH-Question' :
                             pat.type === 'modal_infinitive' ? 'Modal+Infinitive' :
                             pat.type === 'there_is' ? 'There is/are' : 'Phrase Combo'}
                          </span>
                          <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Priority: {pat.priority}</span>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => setEditingPattern({...pat})} className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Edit</button>
                          <button onClick={() => updatePattern(pat.id, { enabled: !pat.enabled })} className={`text-xs px-2 py-1 rounded ${pat.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                            {pat.enabled ? 'ON' : 'OFF'}
                          </button>
                          <button onClick={() => deletePattern(pat.id, pat.name)} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200">Delete</button>
                        </div>
                      </div>
                      <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span className="font-mono bg-gray-100 text-gray-700 px-1 rounded">{pat.trigger}</span>
                        {pat.latin_translation && <span className="ml-2">→ {pat.latin_translation} / {pat.arabic_translation}</span>}
                      </div>
                      {pat.conjugations && (
                        <div className={`mt-2 text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                          Conjugations: {Object.entries(pat.conjugations).map(([pron, val]) => (
                            <span key={pron} className="mr-2">{pron}={val.latin}</span>
                          ))}
                        </div>
                      )}
                      {pat.description && <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{pat.description}</p>}
                      {pat.examples?.length > 0 && pat.examples[0]?.input && (
                        <div className={`mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {pat.examples.filter(e => e.input).map((ex, i) => (
                            <span key={i} className="mr-3">"{ex.input}" → "{ex.expected}"</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Verb + Object Pronoun Patterns ────────────────────────── */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-teal-700' : 'bg-teal-50 border-teal-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <h5 className={`font-medium ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                Verb + Object Patterns ({verbObjectPatterns.length})
              </h5>
              <button onClick={fetchVerbObjectPatterns} className="text-xs px-2 py-1 rounded bg-teal-100 text-teal-700 hover:bg-teal-200">Refresh</button>
            </div>

            {/* Auto-generate form */}
            <div className="flex gap-2 mb-3">
              <input
                value={newVerbObjName}
                onChange={e => setNewVerbObjName(e.target.value)}
                placeholder="Enter verb name (e.g. love, hate, know)"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
              />
              <button
                disabled={verbObjLoading || !newVerbObjName.trim()}
                onClick={async () => {
                  setVerbObjLoading(true);
                  try {
                    const res = await fetch(`${API}/admin/grammar/verb-object-patterns/auto-generate`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                      body: JSON.stringify({ verb: newVerbObjName.trim().toLowerCase() })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      showNotification && showNotification(`Created pattern for "${newVerbObjName}" with ${data.conjugations_count} pronouns`, 'success');
                      setNewVerbObjName('');
                      fetchVerbObjectPatterns();
                    } else {
                      showNotification && showNotification(data.detail || 'Failed to generate', 'error');
                    }
                  } catch (err) {
                    showNotification && showNotification('Error: ' + err.message, 'error');
                  }
                  setVerbObjLoading(false);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-teal-700 hover:bg-teal-600 text-white' : 'bg-teal-600 hover:bg-teal-700 text-white'} disabled:opacity-50`}
              >
                {verbObjLoading ? 'Generating...' : 'Auto-Generate'}
              </button>
            </div>

            {/* Batch Auto-Generate button */}
            <button
              disabled={verbObjLoading}
              onClick={async () => {
                setVerbObjLoading(true);
                showNotification && showNotification('Scanning all verbs... please wait', 'info');
                try {
                  const res = await fetch(`${API}/admin/grammar/verb-object-patterns/batch-auto-generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` }
                  });
                  const data = await res.json();
                  if (res.ok) {
                    showNotification && showNotification(
                      `Done! Created: ${data.created_count}, Existing: ${data.skipped_existing_count}, No data: ${data.skipped_no_data_count}`,
                      data.created_count > 0 ? 'success' : 'info'
                    );
                    fetchVerbObjectPatterns();
                  } else {
                    showNotification && showNotification(data.detail || 'Batch generation failed', 'error');
                  }
                } catch (err) {
                  showNotification && showNotification('Error: ' + err.message, 'error');
                }
                setVerbObjLoading(false);
              }}
              className={`w-full mb-3 px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'} disabled:opacity-50`}
            >
              {verbObjLoading ? 'Generating...' : 'Batch Auto-Generate All Verbs from DB'}
            </button>

            {/* List existing patterns */}
            {verbObjectPatterns.length > 0 && (
              <div className="space-y-2">
                {verbObjectPatterns.map(pat => (
                  <div key={pat._id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600/50 border-gray-500' : 'bg-white border-gray-200'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`font-medium text-sm ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>{pat.trigger}</span>
                        <span className={`ml-2 text-xs ${pat.enabled ? 'text-green-500' : 'text-red-500'}`}>{pat.enabled ? 'enabled' : 'disabled'}</span>
                      </div>
                      <div className="flex gap-1">
                        {editingVerbObj === pat._id ? (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API}/admin/grammar/verb-object-patterns/${pat._id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                                    body: JSON.stringify({ conjugations: editingVerbObjData })
                                  });
                                  if (res.ok) {
                                    showNotification && showNotification('Pattern updated!', 'success');
                                    setEditingVerbObj(null);
                                    fetchVerbObjectPatterns();
                                  } else {
                                    const err = await res.json();
                                    showNotification && showNotification(err.detail || 'Update failed', 'error');
                                  }
                                } catch (err) {
                                  showNotification && showNotification('Error: ' + err.message, 'error');
                                }
                              }}
                              className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                            >Save</button>
                            <button
                              onClick={() => setEditingVerbObj(null)}
                              className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                            >Cancel</button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingVerbObj(pat._id);
                                setEditingVerbObjData(JSON.parse(JSON.stringify(pat.conjugations || {})));
                              }}
                              className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >Edit</button>
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Delete verb+object pattern for "${pat.trigger}"?`)) return;
                                try {
                                  const res = await fetch(`${API}/admin/grammar/verb-object-patterns/${pat._id}`, {
                                    method: 'DELETE',
                                    headers: { 'Authorization': `Bearer ${adminToken}` }
                                  });
                                  if (res.ok) {
                                    showNotification && showNotification('Pattern deleted', 'success');
                                    fetchVerbObjectPatterns();
                                  }
                                } catch (err) {
                                  showNotification && showNotification('Error deleting', 'error');
                                }
                              }}
                              className="text-xs px-2 py-1 rounded bg-red-100 text-red-600 hover:bg-red-200"
                            >Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                    {editingVerbObj === pat._id ? (
                      <div className="mt-2 space-y-1">
                        {['i', 'you', 'he', 'she', 'we', 'they'].map(pron => (
                          <div key={pron} className="flex items-center gap-2 text-xs">
                            <span className="w-10 font-medium">{pron}:</span>
                            <input
                              value={(editingVerbObjData[pron] || {}).latin || ''}
                              onChange={e => setEditingVerbObjData(prev => ({
                                ...prev,
                                [pron]: { ...prev[pron], latin: e.target.value }
                              }))}
                              placeholder="Latin"
                              className={`flex-1 px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                            <input
                              value={(editingVerbObjData[pron] || {}).arabic || ''}
                              onChange={e => setEditingVerbObjData(prev => ({
                                ...prev,
                                [pron]: { ...prev[pron], arabic: e.target.value }
                              }))}
                              placeholder="Arabic"
                              dir="rtl"
                              className={`flex-1 px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      pat.conjugations && (
                        <div className={`mt-2 grid grid-cols-2 gap-1 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {Object.entries(pat.conjugations).map(([pron, val]) => (
                            <div key={pron}>
                              <span className="font-medium">{pron}:</span> {val.latin} / {val.arabic}
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
            {verbObjectPatterns.length === 0 && !verbObjLoading && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No verb+object patterns yet. Enter a verb name and click Auto-Generate.</p>
            )}
          </div>

          {/* ── Verb Subjunctive Forms ──────────────────────────────── */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <h5 className={`font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                Verb Subjunctive Forms ({verbSubjunctives.length})
              </h5>
              <button onClick={fetchSubjunctives} className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200">Refresh</button>
            </div>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              These are the verb forms used after modals (want to, need to, have to). Each verb needs its subjunctive form per pronoun.
              Example: "go" → I: nmshi, You: tmshi, He: ymshi...
            </p>

            {/* Batch Auto-Generate All */}
            <button
              onClick={async () => {
                if (!window.confirm('This will scan ALL translations for "I {verb}" entries and auto-generate subjunctive forms. Continue?')) return;
                try {
                  showNotification && showNotification('Scanning translations... please wait', 'info');
                  const res = await fetch(`${API}/admin/grammar/verb-subjunctives/batch-auto-generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` }
                  });
                  const data = await res.json();
                  if (res.ok) {
                    showNotification && showNotification(
                      `Done! Created: ${data.created_count}, Stubs: ${data.created_stubs_count || 0}, Updated: ${data.updated_count || 0}, Existing: ${data.skipped_existing_count}`,
                      (data.created_count > 0 || (data.created_stubs_count || 0) > 0 || (data.updated_count || 0) > 0) ? 'success' : 'info'
                    );
                    fetchSubjunctives();
                  } else {
                    showNotification && showNotification(data.detail || 'Batch generation failed', 'error');
                  }
                } catch (err) {
                  showNotification && showNotification('Error: ' + err.message, 'error');
                }
              }}
              className={`w-full mb-3 px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
            >
              Batch Auto-Generate All Verbs from DB
            </button>

            {/* Existing verb subjunctives */}
            {subjLoading ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
            ) : verbSubjunctives.length > 0 && (
              <div className="space-y-2 mb-4">
                {verbSubjunctives.map(vs => (
                  <div key={vs.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                    {editingSubj?.id === vs.id ? (
                      /* Edit mode */
                      <div className="space-y-2">
                        <input value={editingSubj.verb} onChange={e => setEditingSubj({...editingSubj, verb: e.target.value})} placeholder="Verb" className={`w-full px-2 py-1 rounded border text-sm font-medium ${isDarkMode ? 'bg-gray-500 border-gray-400 text-white' : 'bg-white border-gray-300'}`} />
                        {['i', 'you', 'he', 'she', 'we', 'they'].map(pron => (
                          <div key={pron} className="grid grid-cols-3 gap-2">
                            <span className={`text-xs py-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{pron.charAt(0).toUpperCase() + pron.slice(1)}:</span>
                            <input
                              value={editingSubj.forms?.[pron]?.latin || ''}
                              onChange={e => setEditingSubj({...editingSubj, forms: {...editingSubj.forms, [pron]: {...(editingSubj.forms?.[pron] || {}), latin: e.target.value}}})}
                              placeholder="Latin"
                              className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-500 border-gray-400 text-white' : 'bg-white border-gray-300'}`}
                            />
                            <input
                              value={editingSubj.forms?.[pron]?.arabic || ''}
                              onChange={e => setEditingSubj({...editingSubj, forms: {...editingSubj.forms, [pron]: {...(editingSubj.forms?.[pron] || {}), arabic: e.target.value}}})}
                              placeholder="Arabic"
                              className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-500 border-gray-400 text-white' : 'bg-white border-gray-300'}`}
                            />
                          </div>
                        ))}
                        <div className="flex gap-2">
                          <button onClick={() => updateSubjunctive(vs.id, editingSubj)} className="px-3 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700">Save</button>
                          <button onClick={() => setEditingSubj(null)} className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      /* View mode */
                      <div>
                        <div className="flex justify-between items-center">
                          <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{vs.verb}</span>
                          <div className="flex gap-1">
                            <button onClick={() => setEditingSubj({...vs})} className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Edit</button>
                            <button onClick={() => deleteSubjunctive(vs.id, vs.verb)} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200">Delete</button>
                          </div>
                        </div>
                        <div className={`mt-1 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {vs.forms && Object.entries(vs.forms).map(([pron, val]) => (
                            <span key={pron} className="mr-3">{pron}: <strong>{val.latin}</strong> / {val.arabic}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add new verb subjunctive */}
            <div className={`p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-purple-600' : 'border-purple-300'}`}>
              <label className={`text-xs font-medium mb-2 block ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Add Verb Subjunctive:</label>
              <div className="flex gap-2 mb-2">
                <input
                  value={newSubj.verb}
                  onChange={e => setNewSubj({...newSubj, verb: e.target.value})}
                  placeholder="English verb (e.g. go, eat, sleep)"
                  className={`flex-1 px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <button
                  onClick={async () => {
                    if (!newSubj.verb.trim()) { showNotification && showNotification('Enter a verb name first', 'error'); return; }
                    try {
                      const res = await fetch(`${API}/admin/grammar/verb-subjunctives/auto-generate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
                        body: JSON.stringify({ verb: newSubj.verb.trim() })
                      });
                      const data = await res.json();
                      if (res.ok && data.forms) {
                        setNewSubj({ ...newSubj, forms: data.forms });
                        showNotification && showNotification(`Auto-generated from "${data.source_entry}" (stem: ${data.stem_latin})`, 'success');
                      } else {
                        showNotification && showNotification(data.detail || 'Could not auto-generate', 'error');
                      }
                    } catch (err) {
                      showNotification && showNotification('Error: ' + err.message, 'error');
                    }
                  }}
                  className="px-3 py-1 bg-purple-500 text-white rounded text-xs font-medium hover:bg-purple-600 whitespace-nowrap"
                >
                  Auto-generate from DB
                </button>
              </div>
              {['i', 'you', 'he', 'she', 'we', 'they'].map(pron => (
                <div key={pron} className="grid grid-cols-3 gap-2 mb-1">
                  <span className={`text-xs py-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{pron.charAt(0).toUpperCase() + pron.slice(1)}:</span>
                  <input
                    value={newSubj.forms[pron]?.latin || ''}
                    onChange={e => setNewSubj({...newSubj, forms: {...newSubj.forms, [pron]: {...newSubj.forms[pron], latin: e.target.value}}})}
                    placeholder="Latin"
                    className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                  />
                  <input
                    value={newSubj.forms[pron]?.arabic || ''}
                    onChange={e => setNewSubj({...newSubj, forms: {...newSubj.forms, [pron]: {...newSubj.forms[pron], arabic: e.target.value}}})}
                    placeholder="Arabic"
                    className={`px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              ))}
              <button
                onClick={saveNewSubjunctive}
                className="w-full mt-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
              >
                Add Verb Subjunctive
              </button>
            </div>
          </div>

          {/* Add New Pattern Form */}
          <div className={`p-4 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-300 bg-gray-50'}`}>
            <h5 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Add New Sentence Pattern</h5>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input
                  value={newPattern.name}
                  onChange={e => setNewPattern({...newPattern, name: e.target.value})}
                  placeholder="Pattern name (e.g. WH: why)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <select
                  value={newPattern.type}
                  onChange={e => setNewPattern({...newPattern, type: e.target.value})}
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="wh_question">WH-Question</option>
                  <option value="modal_infinitive">Modal + Infinitive</option>
                  <option value="there_is">There is/are</option>
                  <option value="phrase_combo">Phrase Combo</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input
                  value={newPattern.trigger}
                  onChange={e => setNewPattern({...newPattern, trigger: e.target.value})}
                  placeholder="Trigger word/phrase (e.g. where)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <input
                  value={newPattern.latin_translation}
                  onChange={e => setNewPattern({...newPattern, latin_translation: e.target.value})}
                  placeholder="Latin Darija (e.g. fin)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <input
                  value={newPattern.arabic_translation}
                  onChange={e => setNewPattern({...newPattern, arabic_translation: e.target.value})}
                  placeholder="Arabic (e.g. فين)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  value={newPattern.description}
                  onChange={e => setNewPattern({...newPattern, description: e.target.value})}
                  placeholder="Description (optional)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
                <input
                  type="number"
                  value={newPattern.priority}
                  onChange={e => setNewPattern({...newPattern, priority: parseInt(e.target.value) || 0})}
                  placeholder="Priority (higher = first)"
                  className={`px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              {/* Examples */}
              <div>
                <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Examples:</label>
                {newPattern.examples.map((ex, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <input
                      value={ex.input}
                      onChange={e => {
                        const updated = [...newPattern.examples];
                        updated[i] = {...updated[i], input: e.target.value};
                        setNewPattern({...newPattern, examples: updated});
                      }}
                      placeholder="Input (e.g. where is the store)"
                      className={`flex-1 px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                    <input
                      value={ex.expected}
                      onChange={e => {
                        const updated = [...newPattern.examples];
                        updated[i] = {...updated[i], expected: e.target.value};
                        setNewPattern({...newPattern, examples: updated});
                      }}
                      placeholder="Expected output"
                      className={`flex-1 px-2 py-1 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                  </div>
                ))}
                <button
                  onClick={() => setNewPattern({...newPattern, examples: [...newPattern.examples, { input: '', expected: '' }]})}
                  className={`text-xs mt-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'} hover:underline`}
                >+ Add example</button>
              </div>
              <button
                onClick={saveNewPattern}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700"
              >
                Add Sentence Pattern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Grammar Section */}
      {activeSection === 'test' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Test Smart Grammar Translation
          </h4>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="e.g., I ask for help, She is bringing food, I love to go"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTestWithRules()}
              className={`flex-1 px-4 py-2 rounded-lg border ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white'
                  : 'bg-white border-gray-300'
              }`}
            />
            <button
              onClick={handleTestWithRules}
              disabled={testLoading || !testText.trim()}
              className={`px-6 py-2 rounded-lg font-medium ${
                testLoading || !testText.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {testLoading ? 'Testing...' : 'Test with Rules'}
            </button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg ${
              testResult.success
                ? isDarkMode ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-emerald-100 border border-emerald-300'
                : isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'
            }`}>
              {testResult.success ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">✅</span>
                    <span className={`font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      Grammar Pattern Found!
                    </span>
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <p><strong>Input:</strong> {testResult.input}</p>
                    {testResult.modified_input && <p><strong>After Rules:</strong> {testResult.modified_input}</p>}
                    <p><strong>Type:</strong> {testResult.grammar_type}</p>
                    <p><strong>Verb Phrase:</strong> "{testResult.verb_phrase}"</p>
                    <p><strong>Translation:</strong> {testResult.verb_translation?.latinScript || testResult.verb_translation?.translatedText}</p>
                    {testResult.object_text && <p><strong>Object:</strong> "{testResult.object_text}" (needs translation)</p>}
                  </div>
                  {/* Applied Rules */}
                  {testResult.applied_rules && testResult.applied_rules.length > 0 && (
                    <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className="text-xs font-bold mb-1">Rules Applied:</p>
                      {testResult.applied_rules.map((r, i) => (
                        <p key={i} className="text-xs">"{r.before}" → "{r.after}" ({r.rule})</p>
                      ))}
                    </div>
                  )}
                  {/* Steps */}
                  {testResult.steps && (
                    <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className="text-xs font-bold mb-1">Pipeline Steps:</p>
                      {testResult.steps.map((s, i) => (
                        <p key={i} className="text-xs">{i + 1}. {s.step}: {s.detail}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    <span className={isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}>
                      {testResult.message}
                    </span>
                  </div>
                  {/* Still show steps even on failure */}
                  {testResult.steps && (
                    <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className="text-xs font-bold mb-1">Pipeline Steps:</p>
                      {testResult.steps.map((s, i) => (
                        <p key={i} className="text-xs">{i + 1}. {s.step}: {s.detail}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Example sentences */}
          <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Try these examples:
            </p>
            <div className="flex flex-wrap gap-2">
              {['I ask', 'He brought', 'We will bring', 'She is asking', 'I ask for help'].map(example => (
                <button
                  key={example}
                  onClick={() => setTestText(example)}
                  className={`px-3 py-1 text-sm rounded ${
                    isDarkMode
                      ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cleanup Section — Skip Characters */}
      {activeSection === 'cleanup' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Text Cleanup — Skip Characters
            </h4>
            <button
              onClick={toggleSkipCharsEnabled}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                skipCharsEnabled
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'
              }`}
            >
              {skipCharsEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Characters added here will be automatically stripped from the end of user input and grammar AI output before translation lookup. For example, if a user types "car." or "car," the trailing punctuation gets removed so "car" is found in the database.
          </p>

          {/* Current skip characters */}
          <div className="mb-4">
            <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Active Skip Characters ({skipChars.length})
            </div>
            {skipChars.length === 0 ? (
              <p className={`text-sm italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No skip characters configured. Add characters below.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {skipChars.map((ch, idx) => (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-mono ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-200 text-gray-800'
                    }`}
                  >
                    <span className="text-lg font-bold">{ch}</span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {ch === '.' ? 'period' : ch === ',' ? 'comma' : ch === '!' ? 'exclamation' : ch === '?' ? 'question' : ch === ';' ? 'semicolon' : ch === ':' ? 'colon' : ch === '"' ? 'quote' : ch === "'" ? 'apostrophe' : `U+${ch.charCodeAt(0).toString(16).toUpperCase()}`}
                    </span>
                    <button
                      onClick={() => removeSkipChar(ch)}
                      className="text-red-500 hover:text-red-400 text-xs font-bold ml-1"
                      title="Remove"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new skip character */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Add Character
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={newSkipChar}
                onChange={(e) => setNewSkipChar(e.target.value)}
                placeholder="Type a character (e.g. . , ! ? ; :)"
                maxLength={2}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkipChar(); } }}
              />
              <button
                onClick={addSkipChar}
                disabled={!newSkipChar.trim()}
                className="px-4 py-2 text-sm font-bold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-all"
              >
                Add
              </button>
            </div>

            {/* Quick add common characters */}
            <div className="mt-3">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quick add: </span>
              {['.', ',', '!', '?', ';', ':'].filter(ch => !skipChars.includes(ch)).map(ch => (
                <button
                  key={ch}
                  onClick={() => { const updated = [...skipChars, ch]; setSkipChars(updated); saveSkipChars(updated, skipCharsEnabled); }}
                  className={`mx-0.5 px-2.5 py-1 text-sm font-mono rounded border transition-all ${
                    isDarkMode ? 'bg-gray-600 border-gray-500 text-white hover:bg-gray-500' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {ch}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Export / Import Section */}
      {activeSection === 'backup' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            💾 Grammar Backup — Export / Import
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Export Card */}
            <div className={`p-5 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-green-700 bg-green-900/20' : 'border-green-300 bg-green-50'}`}>
              <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>📤 Export All Grammar Data</h5>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Download all verb conjugations, grammar rules, and skip characters as a JSON backup file.
              </p>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(`${API}/admin/grammar/export`, {
                      headers: { 'Authorization': `Bearer ${adminToken}` }
                    });
                    if (!response.ok) throw new Error('Export failed');
                    const jsonText = await response.text();
                    const data = JSON.parse(jsonText);
                    const blob = new Blob([jsonText], { type: 'application/json' });
                    const url = window.URL.createObjectURL(blob);
                    const filename = `grammar_backup_${new Date().toISOString().split('T')[0]}.json`;
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    showNotification && showNotification(
                      `Exported ${data.total_verbs || 0} verb entries + ${data.total_rules || 0} rules to ${filename}`,
                      'success'
                    );
                  } catch (err) {
                    showNotification && showNotification('Export failed: ' + err.message, 'error');
                  }
                }}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all"
              >
                Download Backup
              </button>
            </div>

            {/* Import Card */}
            <div className={`p-5 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-blue-700 bg-blue-900/20' : 'border-blue-300 bg-blue-50'}`}>
              <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>📥 Import Grammar Data</h5>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Restore from a JSON backup file. Existing entries are updated, new entries are added. Nothing is deleted.
              </p>
              <input
                type="file"
                accept=".json"
                id="grammar-import-file"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const response = await fetch(`${API}/admin/grammar/import`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${adminToken}` },
                      body: formData
                    });
                    const result = await response.json();
                    if (!response.ok) throw new Error(result.detail || 'Import failed');
                    showNotification && showNotification(
                      `Import complete! ${result.verbs_imported} new, ${result.verbs_updated} updated, ${result.rules_imported} rules`,
                      'success'
                    );
                    fetchVerbs(true);
                  } catch (err) {
                    showNotification && showNotification('Import failed: ' + err.message, 'error');
                  }
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => document.getElementById('grammar-import-file').click()}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                📂 Choose Backup File
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Settings Section */}
      {activeSection === 'settings' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Grammar Settings
          </h4>

          {/* Lock All Verbs Toggle */}
          <div className={`p-5 rounded-lg border ${isDarkMode ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h5 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  Lock All Verbs
                </h5>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  When enabled, all {verbs.length} verbs are protected from editing and deletion. You can still add new verbs.
                </p>
              </div>
              <div className="flex items-center gap-3 ml-4">
                <span className={`text-sm font-medium ${
                  verbs.length > 0 && verbs.every(v => v.locked)
                    ? (isDarkMode ? 'text-amber-400' : 'text-amber-600')
                    : (isDarkMode ? 'text-gray-400' : 'text-gray-500')
                }`}>
                  {verbs.length > 0 && verbs.every(v => v.locked) ? 'Locked' : 'Unlocked'}
                </span>
                <button
                  onClick={async () => {
                    const allLocked = verbs.length > 0 && verbs.every(v => v.locked);
                    const action = allLocked ? 'unlock-all' : 'lock-all';
                    try {
                      const res = await fetch(`${API}/admin/grammar/verbs/${action}`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${adminToken}` }
                      });
                      if (!res.ok) throw new Error('Failed');
                      const data = await res.json();
                      const count = data.entries_locked || data.entries_unlocked || 0;
                      showNotification && showNotification(
                        allLocked ? `Unlocked all verbs (${count} entries)` : `Locked all verbs (${count} entries)`,
                        'success'
                      );
                      fetchVerbs(true);
                    } catch (err) {
                      showNotification && showNotification(`Failed: ${err.message}`, 'error');
                    }
                  }}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    verbs.length > 0 && verbs.every(v => v.locked)
                      ? 'bg-amber-500'
                      : (isDarkMode ? 'bg-gray-600' : 'bg-gray-300')
                  }`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                    verbs.length > 0 && verbs.every(v => v.locked) ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            {/* Lock status details */}
            {verbs.length > 0 && (() => {
              const lockedCount = verbs.filter(v => v.locked).length;
              const unlockedCount = verbs.length - lockedCount;
              return (
                <div className={`mt-4 pt-3 border-t flex gap-4 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                    {lockedCount} locked
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    {unlockedCount} unlocked
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                    {verbs.length} total
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* How Darija Works — Pattern Reference */}
      {activeSection === 'darija-rules' && (
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h4 className={`font-bold mb-4 text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            How Darija Translation Patterns Work
          </h4>
          <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Reference for the smart pattern engine. The system parses English sentences and builds Darija translations using these rules.
          </p>

          {/* Parse Order */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-700/50' : 'bg-indigo-50 border-indigo-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Sentence Parse Order</h5>
            <ol className={`text-sm space-y-1 list-decimal list-inside ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li><strong>Detect pronoun</strong> — I / you / he / she / we / they</li>
              <li><strong>Detect question</strong> — wash / WH-word (fin, shnou, imta, kifash, shkoun, 3lash)</li>
              <li><strong>Detect negation</strong> — don't, doesn't, can't → Ma...sh wrap</li>
              <li><strong>Detect adverb</strong> — always(dima), never(abadan), still(mazal), now(daba), etc.</li>
              <li><strong>Detect modal verb</strong> — want / need / have to / can / like / love → conjugation table</li>
              <li><strong>Skip "to"</strong></li>
              <li><strong>Object pronoun check</strong> — if next word is me/him/her/us/them/it → suffix to modal</li>
              <li><strong>Possessive noun check</strong> — if next words are "my mother"/"his brother" etc. → hardcoded translation</li>
              <li><strong>To-be question fallback</strong> — if is/are/was/were was the question word → copula-drop (Are you with me? → wash nta m3aya)</li>
              <li><strong>Detect verb</strong> → get stem from DB → apply prefix (N/T/Y) + suffix (o)</li>
              <li><strong>Extras</strong> → multi-word adverbs (a lot→bzzaf), with/to/for+pronoun, possessive nouns, DB lookup</li>
            </ol>
          </div>

          {/* Pronoun Verb Prefixes (Subjunctive) */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-emerald-900/20 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>Verb Prefixes (After Modals)</h5>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead><tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 px-3">Pronoun</th><th className="text-left py-2 px-3">Prefix</th><th className="text-left py-2 px-3">Suffix</th><th className="text-left py-2 px-3">Example (khdam)</th>
                </tr></thead>
                <tbody>
                  {[
                    ['I', 'N', '—', 'nkhdam'],
                    ['You', 'T', '—', 'tkhdam'],
                    ['He', 'Y', '—', 'ykhdam'],
                    ['She', 'T', '—', 'tkhdam'],
                    ['We', 'N', 'o', 'nkhdamo'],
                    ['They', '—', 'o', 'khdamo'],
                  ].map(([pron, pre, suf, ex], i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="py-2 px-3 font-medium">{pron}</td><td className="py-2 px-3 font-mono font-bold text-emerald-500">{pre}</td><td className="py-2 px-3 font-mono">{suf}</td><td className="py-2 px-3 font-mono">{ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              <strong>Rule:</strong> Never double consonants — if stem starts with N, don't add N (n3ass stays n3ass). Same for T, M. Applies to both Latin and Arabic script.
            </p>
          </div>

          {/* Present Tense Prefixes */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Present Tense Prefixes (Standalone Verbs)</h5>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead><tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 px-3">Pronoun</th><th className="text-left py-2 px-3">Prefix</th><th className="text-left py-2 px-3">Example (khdam)</th>
                </tr></thead>
                <tbody>
                  {[
                    ['I', 'kan', 'kankhdam'],
                    ['You', 'kat', 'katkhdam'],
                    ['He', 'kay', 'kaykhdam'],
                    ['She', 'kat', 'katkhdam'],
                    ['We', 'kan...o', 'kankhdamo'],
                    ['They', 'kay...o', 'kaykhdamo'],
                  ].map(([pron, pre, ex], i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="py-2 px-3 font-medium">{pron}</td><td className="py-2 px-3 font-mono font-bold text-blue-500">{pre}</td><td className="py-2 px-3 font-mono">{ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal Verb Conjugations */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-purple-900/20 border-purple-700/50' : 'bg-purple-50 border-purple-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Modal Verb Conjugations</h5>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead><tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 px-3">Modal</th><th className="text-left py-2 px-3">I</th><th className="text-left py-2 px-3">You</th><th className="text-left py-2 px-3">He</th><th className="text-left py-2 px-3">She</th><th className="text-left py-2 px-3">We</th><th className="text-left py-2 px-3">They</th>
                </tr></thead>
                <tbody>
                  {[
                    ['want', 'bghit', 'bghiti', 'bgha', 'bghat', 'bghina', 'bghaw'],
                    ['need', 'm7taj', 'm7taj', 'm7taj', 'm7taja', 'm7tajin', 'm7tajin'],
                    ['have to', 'khasni', 'khask', 'khaso', 'khasha', 'khasna', 'khashom'],
                    ['can', 'na9dar', 't9dar', 'y9dar', 't9dar', 'n9dro', 'y9dro'],
                    ['like', 'kay3ajbni', 'kay3ajbek', 'kay3ajbo', 'kay3ajbha', 'kay3ajbna', 'kay3ajbhom'],
                    ['love', 'kanbghi', 'katbghi', 'kaybghi', 'katbghi', 'kanbghiw', 'kaybghiw'],
                  ].map(([modal, ...conjs], i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="py-2 px-3 font-medium">{modal}</td>
                      {conjs.map((c, j) => <td key={j} className="py-2 px-3 font-mono text-xs">{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Should = khasni (same as have to) */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-purple-900/20 border-purple-700/50' : 'bg-purple-50 border-purple-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>"Should" = Same as "Have to" (khasni)</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>"should" and "have to" both use the khasni conjugation. "go" = mchi (NOT sir).</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { en: 'I should go', darija: 'khasni namchi' },
                { en: 'I should not go', darija: 'makhasnich namchi' },
                { en: 'I have to go', darija: 'khasni namchi' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">{'\u2192'}</span>
                  <span className="text-purple-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Possession — 3andi (have + noun, NOT have to) */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-lime-900/20 border-lime-700/50' : 'bg-lime-50 border-lime-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-lime-300' : 'text-lime-700'}`}>Possession — "have" + noun = 3andi (NOT khasni)</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>"have to + verb" = obligation (khasni). "have + noun" = possession (3andi). Negation: ma + 3and + suffix + ch.</p>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead><tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 px-3">Pronoun</th><th className="text-left py-2 px-3">Positive</th><th className="text-left py-2 px-3">Negative</th><th className="text-left py-2 px-3">Example</th>
                </tr></thead>
                <tbody>
                  {[
                    ['I', '3andi', 'ma3andich', 'I have money = 3andi floss'],
                    ['You', '3andek', 'ma3andekch', ''],
                    ['He', '3ando', 'ma3andoch', ''],
                    ['She', '3andha', 'ma3andhach', 'she has money = 3andha floss'],
                    ['We', '3andna', 'ma3andnach', 'we don\'t have time = ma3andnach lwa9t'],
                    ['They', '3andhom', 'ma3andhomch', 'they have food = 3andhom makla'],
                  ].map(([pron, pos, neg, ex], i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="py-2 px-3 font-medium">{pron}</td><td className="py-2 px-3 font-mono font-bold text-lime-500">{pos}</td><td className="py-2 px-3 font-mono text-red-500">{neg}</td><td className="py-2 px-3 font-mono text-xs">{ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Object Pronoun Suffixes */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-rose-900/20 border-rose-700/50' : 'bg-rose-50 border-rose-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-rose-300' : 'text-rose-700'}`}>Object Pronoun Suffixes (me/him/her/us/them)</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>When a modal is followed by an object pronoun, the pronoun is suffixed directly to the conjugated modal — not looked up as a separate verb.</p>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead><tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <th className="text-left py-2 px-3">Object</th><th className="text-left py-2 px-3">Suffix</th><th className="text-left py-2 px-3">Arabic</th><th className="text-left py-2 px-3">Example</th>
                </tr></thead>
                <tbody>
                  {[
                    ['me', 'ni', 'ني', 'katbghi + ni = katbghini (she loves me)'],
                    ['him', 'o', 'و', 'bghit + o = bghito (I want him)'],
                    ['her', 'ha', 'ها', 'kanbghi + ha = kanbghiha (I love her)'],
                    ['us', 'na', 'نا', 'kaybghi + na = kaybghina (he loves us)'],
                    ['them', 'hom', 'هم', 'm7taj + hom = m7tajhom (he needs them)'],
                  ].map(([obj, suf, ar, ex], i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="py-2 px-3 font-medium">{obj}</td><td className="py-2 px-3 font-mono font-bold text-rose-500">{suf}</td><td className="py-2 px-3 font-mono">{ar}</td><td className="py-2 px-3 font-mono text-xs">{ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
              <strong>Rules:</strong> "him" suffix = 'o' after consonant (bghit+o), 'h' after vowel (m7taja+h). Suffix first, then negation wrap (makanbghihash). For "she" add standalone pronoun (hiya katbghini).
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
              <strong>Verb+object:</strong> "I can help them" → na9dar nsa3ad+hom = na9dar nsa3adhom (suffix on verb, not standalone).
            </p>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
              <strong>Indirect object (tell/give):</strong> Uses liya/lik/lih/liha/lina/lihom instead of ni/o/ha. "he told me" = gal+iya = galiya. Double 'l' merges: gal+liya = galiya.
            </p>
          </div>

          {/* Negation */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>Negation: Ma...sh Wrap</h5>
            <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <p><code className="font-mono">bghit</code> → <code className="font-mono text-red-500"><strong>ma</strong>bghit<strong>sh</strong></code> (I don't want)</p>
              <p><code className="font-mono">kay3ajbo</code> → <code className="font-mono text-red-500"><strong>ma</strong>kay3ajbo<strong>ch</strong></code> (he doesn't like)</p>
              <p><code className="font-mono">ymkan</code> → <code className="font-mono text-red-500"><strong>ma</strong>ymkan<strong>ch</strong></code> (can't)</p>
            </div>
          </div>

          {/* Questions */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-amber-900/20 border-amber-700/50' : 'bg-amber-50 border-amber-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Question Words</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                ['where', 'fin / فين'],
                ['what', 'shnou / شنو'],
                ['when', 'imta / إمتى'],
                ['how', 'kifash / كيفاش'],
                ['who', 'shkoun / شكون'],
                ['why', '3lash / علاش'],
                ['yes/no?', 'wash / واش'],
              ].map(([en, da], i) => (
                <div key={i} className={`px-3 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                  <span className="font-medium">{en}</span> → <span className="font-mono text-amber-500">{da}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Prepositions */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-cyan-900/20 border-cyan-700/50' : 'bg-cyan-50 border-cyan-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>Common Prepositions</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                ['in / at', 'f'],
                ['to / for', 'l'],
                ['with', 'm3a'],
                ['from', 'mn'],
                ['on / about', '3la'],
                ['this', 'had'],
                ['that', 'dak'],
              ].map(([en, da], i) => (
                <div key={i} className={`px-3 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                  <span className="font-medium">{en}</span> → <span className="font-mono text-cyan-500">{da}</span>
                </div>
              ))}
            </div>
          </div>

          {/* "To Be" Copula-Drop */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-pink-900/30 border-pink-800' : 'bg-pink-50 border-pink-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-pink-300' : 'text-pink-700'}`}>"To Be" — Copula Drop</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Darija drops am/is/are — just use pronoun + adjective/noun</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { en: 'I am tired', darija: 'ana 3yit' },
                { en: 'She is beautiful', darija: 'hiya zwina' },
                { en: 'He is sick', darija: 'howa mrid' },
                { en: 'They are happy', darija: 'homa far7anin' },
                { en: 'I am a student', darija: 'ana telmid' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">→</span>
                  <span className="text-pink-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
            <p className={`text-xs mt-2 font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              Negation uses "machi" (not ma...sh): "I am not hungry" → ana machi ji3an
            </p>
          </div>

          {/* Future Tense */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-orange-900/30 border-orange-800' : 'bg-orange-50 border-orange-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>Future Tense — ghadi</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>"will" = ghadi + verb prefix (she uses short form "gha")</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { en: 'I will go', darija: 'ghadi namchi' },
                { en: 'She will cook', darija: 'gha tayab' },
                { en: 'I am going to the market', darija: 'ana ghadi lsou9' },
                { en: 'She is cooking', darija: 'hiya katayab' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">→</span>
                  <span className="text-orange-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
          </div>

          {/* "There is/are" */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-teal-900/30 border-teal-800' : 'bg-teal-50 border-teal-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>"There is/are" — kayn/kaynin</h5>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { en: 'There is a cat', darija: 'kayn moch' },
                { en: 'There are many people', darija: 'kaynin bazaf nass' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">→</span>
                  <span className="text-teal-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Conjunctions (Multi-Clause) */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-violet-900/30 border-violet-800' : 'bg-violet-50 border-violet-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>Conjunctions — Multi-Clause Sentences</h5>
            <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Engine splits on conjunctions and translates each clause independently</p>
            <div className="grid grid-cols-3 gap-2 text-sm mb-3">
              {[
                { en: 'because', darija: 'hit / حيت' },
                { en: 'but', darija: 'walakin / ولاكن' },
                { en: 'and', darija: 'w / و' },
                { en: 'or', darija: 'wla / ولا' },
                { en: 'if', darija: 'ila / إلا' },
                { en: 'when', darija: 'mnin / منين' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">=</span>
                  <span className="text-violet-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
            <div className={`text-xs p-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              <strong>Example:</strong> "I want to eat but I am not hungry" → bghit nakol walakin ana machi ji3an
            </div>
          </div>

          {/* Adverbs */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-sky-900/20 border-sky-700/50' : 'bg-sky-50 border-sky-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-sky-300' : 'text-sky-700'}`}>Adverbs</h5>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Detected between negation and modal. Placed before modal/verb in output. Multi-word adverbs (a lot, too much) detected in extras.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {[
                { en: 'always', darija: 'dima / ديما' },
                { en: 'never', darija: 'abadan / ابدا' },
                { en: 'sometimes', darija: "ba3d lmarat / بعد المرات" },
                { en: 'often', darija: 'ghaliban / غالبا' },
                { en: 'usually', darija: "kif l3ada / كيف العادة" },
                { en: 'rarely', darija: '9lil / قليل' },
                { en: 'almost', darija: 'ta9riban / تقريبا' },
                { en: 'still', darija: 'mazal / مازال' },
                { en: 'now', darija: 'daba / دابا' },
                { en: 'a lot', darija: 'bzzaf / بزاف' },
                { en: 'everything', darija: 'kolchi / كلشي' },
                { en: 'nothing', darija: 'ta7aja / تاحاجة' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded text-center text-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">=</span>
                  <span className="text-sky-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
            <div className={`text-xs p-2 mt-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              <strong>Example:</strong> "she always love me" → hiya dima katbghini | "I love her a lot" → kanbghiha bzzaf
            </div>
          </div>

          {/* With + Pronoun */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-teal-900/20 border-teal-700/50' : 'bg-teal-50 border-teal-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>Preposition + Pronoun (with / to / for)</h5>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Preposition + pronoun = single fused word. Also works in to-be questions: "Are you with me?" → "wash nta m3aya?"
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {[
                { en: 'with me', darija: "m3aya / معايا" },
                { en: 'with you', darija: "m3ak / معاك" },
                { en: 'with him', darija: "m3ah / معاه" },
                { en: 'with her', darija: "m3aha / معاها" },
                { en: 'with us', darija: "m3ana / معانا" },
                { en: 'with them', darija: "m3ahom / معاهم" },
                { en: 'to me', darija: "liya / ليا" },
                { en: 'to you', darija: "lik / ليك" },
                { en: 'to him', darija: "lih / ليه" },
                { en: 'to her', darija: "liha / ليها" },
                { en: 'to us', darija: "lina / لينا" },
                { en: 'to them', darija: "lihom / ليهم" },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded text-center text-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">=</span>
                  <span className="text-teal-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
            <div className={`text-xs p-2 mt-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              <strong>Example:</strong> "I want to go with her" → bghit nmchi m3aha | "Are you with me?" → wash nta m3aya
            </div>
          </div>

          {/* Possessive Family Nouns */}
          <div className={`mb-6 p-4 rounded-lg border ${isDarkMode ? 'bg-amber-900/20 border-amber-700/50' : 'bg-amber-50 border-amber-200'}`}>
            <h5 className={`font-semibold mb-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Possessive Family Nouns</h5>
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              "my/his/her + family" = single hardcoded Darija word (possessive already built-in). Detected both in verb position (after modal) and in extras.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {[
                { en: 'my mother', darija: 'walida / والدة' },
                { en: 'my mom', darija: 'mi / مي' },
                { en: 'my father', darija: 'walid / واليد' },
                { en: 'my dad', darija: 'bba / ببا' },
                { en: 'my brother', darija: 'khoya / خويا' },
                { en: 'my sister', darija: 'khti / ختي' },
                { en: 'my wife', darija: 'mrati / مراتي' },
                { en: 'my husband', darija: 'rajli / راجلي' },
                { en: 'his brother', darija: 'khouh / خوه' },
                { en: 'his mother', darija: 'walidto / والدتو' },
                { en: 'her brother', darija: 'khouha / خوها' },
              ].map((ex, i) => (
                <div key={i} className={`px-2 py-1 rounded text-center text-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ex.en}</span>
                  <span className="mx-1">=</span>
                  <span className="text-amber-500 font-mono font-bold">{ex.darija}</span>
                </div>
              ))}
            </div>
            <div className={`text-xs p-2 mt-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
              <strong>Example:</strong> "I love my mother" → kanbghi walida | "he love his brother" → kaybghi khouh
            </div>
          </div>

          {/* Worked Examples */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <h5 className={`font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Worked Examples</h5>
            <div className="space-y-3">
              {[
                { en: 'I want to go', parse: 'I(pron) + want(modal) + to(skip) + go(verb)', result: 'bghit nmshi' },
                { en: "I don't want to eat in the kitchen", parse: "I + don't(neg) + want + to + eat + in + the(strip) + kitchen", result: 'mabghitsh nakol f kozina' },
                { en: 'I am tired', parse: 'I(pron) + am(to-be) + tired(adj) → copula-drop', result: 'ana 3yit' },
                { en: 'I am not hungry', parse: 'I + am(to-be) + not(neg) + hungry → machi', result: 'ana machi ji3an' },
                { en: 'I will go tomorrow', parse: 'I + will(future→ghadi) + go(verb)', result: 'ghadi namchi gheda' },
                { en: 'She is cooking', parse: 'She + is(to-be) + cooking(-ing→cook) → present prefix', result: 'hiya katayab' },
                { en: 'There is a cat', parse: 'there is → kayn + cat(DB)', result: 'kayn moch' },
                { en: 'I need to go because I am tired', parse: 'clause1 + because(hit) + clause2', result: 'm7taj nmchi hit ana 3yit' },
                { en: 'I love her', parse: 'I(pron) + love(modal) + her(obj-suffix) → kanbghi+ha', result: 'kanbghiha' },
                { en: 'she love me', parse: 'she(pron,ambiguous) + love(modal) + me(obj-suffix) → hiya + katbghi+ni', result: 'hiya katbghini' },
                { en: 'I love her but she love me', parse: 'clause1(obj-suffix) + but(walakin) + clause2(obj-suffix)', result: 'kanbghiha walakin hiya katbghini' },
                { en: 'she needs him', parse: 'she(pron) + need(modal,m7taja) + him(obj,vowel→h)', result: 'hiya m7tajah' },
                { en: 'I can help them', parse: 'I + can(modal) + help(verb) + them(obj→verb suffix)', result: 'na9dar nsa3adhom' },
                { en: 'I miss her', parse: 'I(pron) + miss(past-verb,twa7acht) + her(obj+ha)', result: 'twa7achtha' },
                { en: 'she told me', parse: 'she(pron) + told(past-verb,gat) + me(indirect→liya)', result: 'hiya gatliya' },
                { en: 'he told me', parse: 'he + told(gal) + me(indirect,l-merge→iya)', result: 'galiya' },
                { en: 'Where do they work?', parse: 'Where(fin) + do they work(present)', result: 'fin kaykhadmo' },
                { en: "Let's go to the market", parse: "Let's(yalah) + go(we-form) + the(strip) + market", result: 'yalah namshiw sou9' },
                { en: 'I love her a lot', parse: 'I + love(modal) + her(obj→ha) + a lot(multi-adverb→bzzaf)', result: 'kanbghiha bzzaf' },
                { en: 'she always love me', parse: 'she + always(adverb→dima) + love(modal) + me(obj→ni)', result: 'hiya dima katbghini' },
                { en: 'I love my mother', parse: 'I + love(modal) + my mother(possessive→walida)', result: 'kanbghi walida' },
                { en: 'he love his brother', parse: 'he + love(modal) + his brother(possessive→khouh)', result: 'kaybghi khouh' },
                { en: 'I want to go with her', parse: 'I + want(modal) + to(skip) + go(verb) + with her(→m3aha)', result: 'bghit nmchi m3aha' },
                { en: 'I miss my sister', parse: 'I + miss(past-verb→twa7acht) + my sister(possessive→khti)', result: 'twa7acht khti' },
                { en: 'Are you with me?', parse: 'Are(to-be-question) + you(pron) + with me(→m3aya) → wash + nta + m3aya', result: 'wash nta m3aya' },
                { en: 'Is she with him?', parse: 'Is(to-be-question) + she(pron) + with him(→m3ah)', result: 'wash hiya m3ah' },
                { en: 'I want to go to her', parse: 'I + want(modal) + to(skip) + go(verb) + to her(→liha)', result: 'bghit nmchi liha' },
                { en: 'I have money', parse: 'I(pron) + have(possession) + money(DB) → 3andi', result: '3andi floss' },
                { en: 'I don\'t have money', parse: 'I + do(aux) + not(neg) + have(possession) → ma3andich', result: 'ma3andich floss' },
                { en: 'they have food', parse: 'they(pron) + have(possession) + food(DB)', result: '3andhom makla' },
                { en: 'I should go', parse: 'I(pron) + should(modal→khasni) + go(verb)', result: 'khasni namchi' },
                { en: 'I should not go', parse: 'I + should(modal) + not(neg) + go → makhasnich', result: 'makhasnich namchi' },
                { en: 'I have to go', parse: 'I + have to(modal→khasni) + go(verb)', result: 'khasni namchi' },
              ].map((ex, i) => (
                <div key={i} className={`p-3 rounded border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                  <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ex.en}</div>
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{ex.parse}</div>
                  <div className="text-sm mt-1 font-mono text-emerald-500 font-bold">{ex.result}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Generate Negatives Modal */}
      {negModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden transform transition-all ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 ${isDarkMode ? 'bg-red-900/40' : 'bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🔄</div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      Generate {negModal.tense ? negModal.tense.charAt(0).toUpperCase() + negModal.tense.slice(1) : ''} Negatives
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Teach the system the negation pattern
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNegModal(null)}
                  className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Positive example display */}
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  Positive form (from your data):
                </div>
                <div className={`text-lg font-bold font-mono ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  {negModal.positiveLatin}
                </div>
              </div>

              {/* Quick suggestion */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Suggestion:</span>
                <button
                  onClick={() => setNegModalInput(`ma${negModal.positiveLatin}ch`)}
                  className={`px-3 py-1.5 rounded-full text-sm font-mono font-bold border-2 border-dashed transition-all hover:scale-105 ${
                    isDarkMode
                      ? 'border-red-600 text-red-300 bg-red-900/20 hover:bg-red-900/40'
                      : 'border-red-300 text-red-600 bg-red-50 hover:bg-red-100'
                  }`}
                >
                  ma{negModal.positiveLatin}ch
                </button>
              </div>

              {/* Arrow */}
              <div className="text-center text-2xl mb-3">⬇️</div>

              {/* Negative input */}
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                <label className={`text-xs font-medium mb-2 block ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  What is the NEGATIVE of "<strong>{negModal.positiveLatin}</strong>"? (verb only, no pronoun)
                </label>
                <input
                  type="text"
                  value={negModalInput}
                  onChange={(e) => setNegModalInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && negModalInput.trim() && !negModal.generating) {
                      // Trigger generate
                      document.getElementById('neg-modal-generate-btn')?.click();
                    }
                  }}
                  placeholder="e.g. manakoch, maymknch..."
                  autoFocus
                  className={`w-full px-4 py-3 rounded-lg border-2 text-lg font-mono focus:outline-none focus:ring-2 ${
                    isDarkMode
                      ? 'bg-gray-700 border-red-700 text-white placeholder-gray-500 focus:ring-red-500 focus:border-red-500'
                      : 'bg-white border-red-300 text-gray-800 placeholder-gray-400 focus:ring-red-400 focus:border-red-400'
                  }`}
                />
              </div>

              {/* Hint */}
              <div className={`text-xs mb-4 p-2 rounded ${isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                Type the <strong>verb only</strong> — no pronoun! Example: <strong>{negModal.positiveLatin}</strong> {"=>"} <strong>ma{negModal.positiveLatin}ch</strong>.
                The system keeps each pronoun (Ana, Howa, Hiya...) and wraps only the verb with your pattern.
              </div>

              {/* Drop pronoun toggle */}
              <label className={`flex items-center gap-2 mb-3 cursor-pointer select-none text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <input
                  type="checkbox"
                  checked={negDropPronoun}
                  onChange={(e) => setNegDropPronoun(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                Remove pronoun (Ana, Howa, Hiya...) — output verb only
              </label>

              {/* Per-pronoun checkboxes */}
              <div className={`mb-4 p-3 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Include pronouns (uncheck to exclude from generation):
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { key: 'i', label: 'I (Ana)' },
                    { key: 'you_sm', label: 'You SM (Anta)' },
                    { key: 'you_sf', label: 'You SF (Anti)' },
                    { key: 'he', label: 'He (Howa)' },
                    { key: 'she', label: 'She (Hiya)' },
                    { key: 'it', label: 'It' },
                    { key: 'we', label: 'We (Hna)' },
                    { key: 'you_p', label: 'You P (Ntoma)' },
                    { key: 'they', label: 'They (Homa)' },
                  ].map(p => (
                    <label key={p.key} className={`flex items-center gap-1.5 cursor-pointer select-none text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        checked={!negExcludePronouns.has(p.key)}
                        onChange={(e) => {
                          setNegExcludePronouns(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.delete(p.key);
                            else next.add(p.key);
                            return next;
                          });
                        }}
                        className="w-3.5 h-3.5 rounded"
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setNegModal(null)}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  id="neg-modal-generate-btn"
                  disabled={!negModalInput.trim() || negModal.generating}
                  onClick={async () => {
                    setNegModal(prev => ({ ...prev, generating: true }));
                    try {
                      const res = await fetch(`${API}/admin/grammar/verbs/generate-negatives`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          base_verb: negModal.verbName,
                          example_positive: negModal.positiveLatin,
                          example_negative: negModalInput.trim(),
                          tense: negModal.tense || '',
                          drop_pronoun: negDropPronoun
                        })
                      });
                      const result = await res.json();
                      if (!res.ok) {
                        showNotification && showNotification(result.detail || 'Generation failed', 'error');
                        setNegModal(prev => ({ ...prev, generating: false }));
                        return;
                      }
                      // Filter out excluded pronouns
                      const pronounPrefixMap = { i: 'i ', he: 'he ', she: 'she ', it: 'it ', we: 'we ', they: 'they ', you_sm: null, you_sf: null, you_p: null };
                      const filtered = result.proposed.filter(p => {
                        const src = (p.sourceText || '').toLowerCase();
                        for (const key of negExcludePronouns) {
                          if (key === 'i' && src.startsWith('i ')) return false;
                          if (key === 'he' && src.startsWith('he ')) return false;
                          if (key === 'she' && src.startsWith('she ')) return false;
                          if (key === 'it' && src.startsWith('it ')) return false;
                          if (key === 'we' && src.startsWith('we ')) return false;
                          if (key === 'they' && src.startsWith('they ')) return false;
                          if (key === 'you_sm' && src.startsWith('you ') && p.gender === 'male' && p.number === 'singular') return false;
                          if (key === 'you_sf' && src.startsWith('you ') && p.gender === 'female' && p.number === 'singular') return false;
                          if (key === 'you_p' && src.startsWith('you ') && p.number === 'plural') return false;
                        }
                        return true;
                      });
                      const genTense = negModal.tense || 'present';
                      setNegativeProposals(prev => ({...prev, [genTense]: filtered}));
                      setNegativePattern(prev => ({...prev, [genTense]: result.pattern}));
                      setNegModal(null);
                      showNotification && showNotification(`Generated ${filtered.length} negative proposals — review below!`, 'success');
                    } catch (err) {
                      showNotification && showNotification('Error: ' + err.message, 'error');
                      setNegModal(prev => ({ ...prev, generating: false }));
                    }
                  }}
                  className="flex-1 py-3 rounded-lg font-bold text-white transition-all bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {negModal.generating ? 'Generating...' : `Generate ${negModal.tense ? negModal.tense.charAt(0).toUpperCase() + negModal.tense.slice(1) : ''} Negatives`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Questions Modal */}
      {qModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backdropFilter: 'blur(6px)', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
          <div className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden transform transition-all ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 ${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">?</div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      Generate {qModal.tense ? qModal.tense.charAt(0).toUpperCase() + qModal.tense.slice(1) : ''} Questions
                    </h3>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Teach the system the question pattern
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setQModal(null)}
                  className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                >
                  {'\u2715'}
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Positive example display */}
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-emerald-50 border border-emerald-200'}`}>
                <div className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  Positive form (from your data):
                </div>
                <div className={`text-lg font-bold font-mono ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  {qModal.positiveLatin}
                </div>
              </div>

              {/* Quick suggestion */}
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Suggestion:</span>
                <button
                  onClick={() => setQModalInput(`wach ${qModal.positiveLatin}?`)}
                  className={`px-3 py-1.5 rounded-full text-sm font-mono font-bold border-2 border-dashed transition-all hover:scale-105 ${
                    isDarkMode
                      ? 'border-blue-600 text-blue-300 bg-blue-900/20 hover:bg-blue-900/40'
                      : 'border-blue-300 text-blue-600 bg-blue-50 hover:bg-blue-100'
                  }`}
                >
                  wach {qModal.positiveLatin}?
                </button>
              </div>

              {/* Arrow */}
              <div className="text-center text-2xl mb-3">?</div>

              {/* Question input */}
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
                <label className={`text-xs font-medium mb-2 block ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  What is the QUESTION form of "<strong>{qModal.positiveLatin}</strong>"? (verb only, no pronoun)
                </label>
                <input
                  type="text"
                  value={qModalInput}
                  onChange={(e) => setQModalInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && qModalInput.trim() && !qModal.generating) {
                      document.getElementById('q-modal-generate-btn')?.click();
                    }
                  }}
                  placeholder="e.g. wach kan9bal?, wach kayakol?..."
                  autoFocus
                  className={`w-full px-4 py-3 rounded-lg border-2 text-lg font-mono focus:outline-none focus:ring-2 ${
                    isDarkMode
                      ? 'bg-gray-700 border-blue-700 text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-blue-300 text-gray-800 placeholder-gray-400 focus:ring-blue-400 focus:border-blue-400'
                  }`}
                />
              </div>

              {/* Hint */}
              <div className={`text-xs mb-4 p-2 rounded ${isDarkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                Type the <strong>verb only</strong> with question pattern. Example: <strong>{qModal.positiveLatin}</strong> {"=>"} <strong>wach {qModal.positiveLatin}?</strong>.
                The system keeps each pronoun (Ana, Howa, Hiya...) and wraps only the verb with your pattern.
              </div>

              {/* Drop pronoun toggle */}
              <label className={`flex items-center gap-2 mb-3 cursor-pointer select-none text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <input
                  type="checkbox"
                  checked={qDropPronoun}
                  onChange={(e) => setQDropPronoun(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                Remove pronoun (Ana, Howa, Hiya...) — output verb only
              </label>

              {/* Per-pronoun checkboxes */}
              <div className={`mb-4 p-3 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Include pronouns (uncheck to exclude from generation):
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { key: 'i', label: 'I (Ana)' },
                    { key: 'you_sm', label: 'You SM (Anta)' },
                    { key: 'you_sf', label: 'You SF (Anti)' },
                    { key: 'he', label: 'He (Howa)' },
                    { key: 'she', label: 'She (Hiya)' },
                    { key: 'it', label: 'It' },
                    { key: 'we', label: 'We (Hna)' },
                    { key: 'you_p', label: 'You P (Ntoma)' },
                    { key: 'they', label: 'They (Homa)' },
                  ].map(p => (
                    <label key={p.key} className={`flex items-center gap-1.5 cursor-pointer select-none text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        checked={!qExcludePronouns.has(p.key)}
                        onChange={(e) => {
                          setQExcludePronouns(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.delete(p.key);
                            else next.add(p.key);
                            return next;
                          });
                        }}
                        className="w-3.5 h-3.5 rounded"
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setQModal(null)}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  id="q-modal-generate-btn"
                  disabled={!qModalInput.trim() || qModal.generating}
                  onClick={async () => {
                    setQModal(prev => ({ ...prev, generating: true }));
                    try {
                      const res = await fetch(`${API}/admin/grammar/verbs/generate-questions`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          base_verb: qModal.verbName,
                          example_positive: qModal.positiveLatin,
                          example_question: qModalInput.trim(),
                          tense: qModal.tense || '',
                          drop_pronoun: qDropPronoun
                        })
                      });
                      const result = await res.json();
                      if (!res.ok) {
                        showNotification && showNotification(result.detail || 'Generation failed', 'error');
                        setQModal(prev => ({ ...prev, generating: false }));
                        return;
                      }
                      // Filter out excluded pronouns
                      const filtered = result.proposed.filter(p => {
                        const src = (p.sourceText || '').toLowerCase();
                        for (const key of qExcludePronouns) {
                          if (key === 'i' && (src.startsWith('do i ') || src.startsWith('did i ') || src.startsWith('will i ') || src.startsWith('am i '))) return false;
                          if (key === 'he' && (src.startsWith('does he ') || src.startsWith('did he ') || src.startsWith('will he ') || src.startsWith('is he '))) return false;
                          if (key === 'she' && (src.startsWith('does she ') || src.startsWith('did she ') || src.startsWith('will she ') || src.startsWith('is she '))) return false;
                          if (key === 'it' && (src.startsWith('does it ') || src.startsWith('did it ') || src.startsWith('will it ') || src.startsWith('is it '))) return false;
                          if (key === 'we' && (src.startsWith('do we ') || src.startsWith('did we ') || src.startsWith('will we ') || src.startsWith('are we '))) return false;
                          if (key === 'they' && (src.startsWith('do they ') || src.startsWith('did they ') || src.startsWith('will they ') || src.startsWith('are they '))) return false;
                          if (key === 'you_sm' && src.startsWith('do you ') && p.gender === 'male' && p.number === 'singular') return false;
                          if (key === 'you_sf' && src.startsWith('do you ') && p.gender === 'female' && p.number === 'singular') return false;
                          if (key === 'you_p' && src.startsWith('do you ') && p.number === 'plural') return false;
                        }
                        return true;
                      });
                      const genTenseQ = qModal.tense || 'present';
                      setQuestionProposals(prev => ({...prev, [genTenseQ]: filtered}));
                      setQuestionPattern(prev => ({...prev, [genTenseQ]: result.pattern}));
                      setQModal(null);
                      showNotification && showNotification(`Generated ${filtered.length} question proposals — review below!`, 'success');
                    } catch (err) {
                      showNotification && showNotification('Error: ' + err.message, 'error');
                      setQModal(prev => ({ ...prev, generating: false }));
                    }
                  }}
                  className="flex-1 py-3 rounded-lg font-bold text-white transition-all bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {qModal.generating ? 'Generating...' : `Generate ${qModal.tense ? qModal.tense.charAt(0).toUpperCase() + qModal.tense.slice(1) : ''} Questions`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Verb Modal */}
      {dupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className={`rounded-2xl shadow-2xl w-full max-w-md p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Duplicate Verb: <span className="text-indigo-500">{dupModal.base_verb?.toUpperCase()}</span>
              </h3>
              <button onClick={() => setDupModal(null)} className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>{'✕'}</button>
            </div>
            <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Will duplicate <strong>{dupModal.total_conjugations}</strong> entries
              {dupModal.positive_count > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>+{dupModal.positive_count}</span>}
              {dupModal.negative_count > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'}`}>-{dupModal.negative_count}</span>}
              {dupModal.question_count > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>Q{dupModal.question_count}</span>}
            </p>
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              English will change, Darija stays the same.
            </p>
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>New English verb name</label>
              <input
                type="text"
                value={dupNewName}
                onChange={(e) => setDupNewName(e.target.value.toLowerCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && dupNewName.trim() && !dupLoading) {
                    document.getElementById('dup-confirm-btn')?.click();
                  }
                }}
                placeholder={`e.g., invite`}
                autoFocus
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDupModal(null)} className={`flex-1 py-2.5 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Cancel</button>
              <button
                id="dup-confirm-btn"
                disabled={!dupNewName.trim() || dupNewName.trim() === dupModal.base_verb || dupLoading}
                onClick={async () => {
                  try {
                    setDupLoading(true);
                    const res = await fetch(`${API}/admin/grammar/verbs/duplicate`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ source_verb: dupModal.base_verb, new_verb: dupNewName.trim() })
                    });
                    const result = await res.json();
                    if (!res.ok) { showNotification && showNotification(result.detail || 'Duplicate failed', 'error'); return; }
                    showNotification && showNotification(`Duplicated ${result.cloned_count} entries: "${dupModal.base_verb}" → "${dupNewName.trim()}"`, 'success');
                    setDupModal(null);
                    setDupNewName('');
                    fetchVerbs(true);
                  } catch (err) {
                    showNotification && showNotification('Error: ' + err.message, 'error');
                  } finally {
                    setDupLoading(false);
                  }
                }}
                className="flex-1 py-2.5 rounded-lg font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dupLoading ? 'Duplicating...' : `Duplicate → ${dupNewName.trim() || '...'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog Modal */}
      {confirmDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
          <div className={`max-w-sm w-full rounded-2xl shadow-2xl p-6 transform transition-all ${
            isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <div className="text-center">
              <div className="text-4xl mb-3">{confirmDialog.icon || '⚠️'}</div>
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {confirmDialog.title || 'Confirm'}
              </h3>
              <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {confirmDialog.message}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDialog.onCancel}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all ${
                    isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDialog.onConfirm}
                  className={`flex-1 py-2.5 rounded-lg font-medium transition-all text-white ${
                    confirmDialog.confirmColor === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {confirmDialog.confirmText || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrammarAdminTab;
